import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import WelcomeMessage from '../components/WelcomeMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import UserDialog from '../components/UserDialog';
import LessonFinancialFields from '../components/LessonFinancialFields';
import '../force-desktop.css';

import { API_BASE_URL } from '../config/api';
import { getBookedResourceRate, getLocationOverride, isLocationScheduleAvailable, schoolWallClockToIso } from '../utils/locationSchedule';
import { getAircraftLocationAvailabilityError } from '../utils/locationScheduleReconciliation';
import { getAssistantSchedulerAction } from '../utils/assistant';
import { applyReservationDuration, findReservationType, legacyReservationType } from '../utils/reservationTypes';
import { getLocationDisplayName } from '../utils/locationUtils';
import { canCancelReservationAtShortNotice, requiresReservationReason } from '../utils/reservationChangePolicy';

const instructorDetailLabelStyle = { width: '42%', padding: '0.5rem 1rem 0.5rem 0', textAlign: 'left', verticalAlign: 'top', color: '#526671', fontWeight: 600 };
const instructorDetailValueStyle = { padding: '0.5rem 0', verticalAlign: 'top', color: '#263943', overflowWrap: 'anywhere' };


const CalendarView = () => {
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('day');
  const hasMountedScheduleView = React.useRef(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const { instructors: allInstructors, aircraft: allAircraft, reservations: allReservations, people, roles, maneuvers, addReservation, fetchInstructors, fetchUsers, fetchAircraft, fetchReservations, currentUser, currentOrg, settings, locations, selectedLocationId, setSelectedLocationId, locationResources, locationError, instructorError, reservationTypes = [], fetchReservationTypes, reservationTypesError, loadingReservationTypes,
    locationAircraftSchedules, locationAircraftSchedulesError, loadingLocationAircraftSchedules, fetchLocationAircraftSchedules } = useApp();
  const aircraftLocationError = (aircraftId, start, end, locationId = selectedLocationId) => getAircraftLocationAvailabilityError({
    aircraftId, locationId, start, end, locations, schedules: locationAircraftSchedules,
    error: locationAircraftSchedulesError, loading: loadingLocationAircraftSchedules
  });
  const locationResourcesReady = String(locationResources.locationId) === String(selectedLocationId);
  const selectedLocation = locations.find(item => String(item.id) === String(selectedLocationId));
  const instructors = React.useMemo(() => {
    const scoped = locationResourcesReady ? locationResources.instructors : [];
    const peopleById = new Map((Array.isArray(people) ? people : []).map(person => [String(person.id), person]));
    const inactiveIds = new Set([
      ...peopleById.values(),
      ...(Array.isArray(allInstructors) ? allInstructors : []),
      ...scoped
    ].filter(person => String(person.status || '').trim().toLowerCase() === 'inactive').map(person => String(person.id)));
    const applyOrganizationStatus = instructor => ({
      ...instructor,
      status: inactiveIds.has(String(instructor.id)) ? 'inactive' : peopleById.get(String(instructor.id))?.status ?? instructor.status
    });
    const isActive = instructor => String(instructor.status || '').trim().toLowerCase() === 'active';
    if (selectedLocationId && !selectedLocation?.isDefault) return scoped.map(applyOrganizationStatus).filter(isActive);
    const roleList = Array.isArray(roles) ? roles : Object.values(roles || {});
    const instructorPeople = (Array.isArray(people) ? people : []).filter(person => {
      const role = roleList.find(item => Number(item.id) === Number(person.roleId ?? person.role_id));
      return Number(person.roleId ?? person.role_id) === 3
        || String(role?.name || person.role || '').toLowerCase() === 'instructor'
        || String(person.orgRole || '').toLowerCase() === 'instructor'
        || [true, 1, '1', 'true'].includes(person.isInstructor);
    });
    const combined = new Map(instructorPeople.map(person => [String(person.id), person]));
    for (const instructor of Array.isArray(allInstructors) ? allInstructors : []) {
      combined.set(String(instructor.id), { ...combined.get(String(instructor.id)), ...instructor });
    }
    return [...combined.values()]
      .map(instructor => applyOrganizationStatus({ ...instructor, ...scoped.find(item => Number(item.id) === Number(instructor.id)) }))
      .filter(isActive);
  }, [allInstructors, people, roles, selectedLocationId, selectedLocation?.isDefault, locationResourcesReady, locationResources.instructors]);
  const aircraft = React.useMemo(() => {
    if (!selectedLocationId) return allAircraft;
    const scoped = locationResourcesReady ? locationResources.aircraft : [];
    if (!selectedLocation?.isDefault) return scoped;
    return allAircraft.map(plane => ({ ...plane, ...scoped.find(item => Number(item.id) === Number(plane.id)) }));
  }, [allAircraft, selectedLocationId, selectedLocation?.isDefault, locationResourcesReady, locationResources.aircraft]);
  const reservations = React.useMemo(() => selectedLocationId
    ? allReservations.filter(item => String(item.locationId || (locations.find(place => place.isDefault)?.id)) === String(selectedLocationId))
    : allReservations, [selectedLocationId, allReservations, locations]);
  const [workSchedules, setWorkSchedules] = useState({});
  const getBookedRate = (resourceType, resourceId, fallback) => getBookedResourceRate(editingReservation, resourceType, resourceId, fallback);
  const [aircraftBlockouts, setAircraftBlockouts] = useState([]);
  const [googleCalendarBanner, setGoogleCalendarBanner] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const assistantRequestRef = React.useRef(null);
  const [assistantSchedulerError, setAssistantSchedulerError] = useState('');

  React.useEffect(() => {
    if (!hasMountedScheduleView.current) {
      hasMountedScheduleView.current = true;
      return;
    }
    if (view === 'day') {
      window.dispatchEvent(new Event('popup-question:schedule-day-viewed'));
    }
  }, [date, view]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const googleCalendarResult = params.get('googleCalendar');

    if (!googleCalendarResult) {
      return;
    }

    if (googleCalendarResult === 'connected') {
      setGoogleCalendarBanner({ type: 'success', text: 'Google Calendar connected. Your reservations will now sync automatically.' });
    } else if (googleCalendarResult === 'error') {
      setGoogleCalendarBanner({ type: 'error', text: params.get('message') || 'Unable to connect Google Calendar.' });
    }

    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search]);
  const instructionOwnerAircraftFee = React.useMemo(() => {
    const rawSettings = Array.isArray(settings) ? settings[0] : settings;
    const parsedFee = Number(rawSettings?.instructionOwnerAircraftFee);
    return Number.isFinite(parsedFee) ? parsedFee : 10;
  }, [settings]);
  const toSortOrderValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : Number.MAX_SAFE_INTEGER;
  };

  const fetchAircraftBlockouts = React.useCallback(async () => {
    const orgId = currentUser?.user?.orgId;
    if (!orgId || !currentUser?.sessionId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-blackout/org/${orgId}`, {
        headers: { 'session-id': currentUser.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setAircraftBlockouts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      setAircraftBlockouts([]);
    }
  }, [currentUser?.user?.orgId, currentUser?.sessionId]);

  const isAircraftBlockedOut = React.useCallback((aircraftId, dateStr, timeSlot) => {
    const [hour, minute] = timeSlot.split(':').map(Number);
    const slotDate = new Date(`${dateStr}T${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}:00`);
    return aircraftBlockouts.some(b => {
      // eslint-disable-next-line eqeqeq
      if (b.aircraftId != aircraftId) return false;
      const start = new Date(b.startDatetime);
      const end = new Date(b.endDatetime);
      return slotDate >= start && slotDate < end;
    });
  }, [aircraftBlockouts]);

  const getAircraftBlockoutForDate = React.useCallback((aircraftId, dateStr) => {
    return aircraftBlockouts.filter(b => {
      // eslint-disable-next-line eqeqeq
      if (b.aircraftId != aircraftId) return false;
      const start = new Date(b.startDatetime);
      const end = new Date(b.endDatetime);
      const day = new Date(dateStr + 'T00:00:00');
      const dayEnd = new Date(dateStr + 'T23:59:59');
      return start <= dayEnd && end >= day;
    });
  }, [aircraftBlockouts]);

  const getDateRange = () => {
    if (view === 'day') {
      const dateStr = getDateString(date);
      return { startDate: dateStr, endDate: dateStr };
    } else if (view === 'week') {
      const weekDates = getWeekDates(date);
      return { 
        startDate: getDateString(weekDates[0]), 
        endDate: getDateString(weekDates[6]) 
      };
    } else {
      const monthDates = getMonthDates(date);
      return { 
        startDate: getDateString(monthDates[0]), 
        endDate: getDateString(monthDates[41]) 
      };
    }
  };

  const scheduleHoursRefreshKey = `${view}-${date.toISOString()}`;

  React.useEffect(() => {
    fetchInstructors();
    fetchUsers();
    fetchAircraft();
  }, [fetchInstructors, fetchUsers, fetchAircraft]);

  React.useEffect(() => {
    fetchAircraftBlockouts();
  }, [fetchAircraftBlockouts]);

  const fetchWorkSchedules = async () => {
    if (selectedLocationId) {
      setWorkSchedules(Object.fromEntries(instructors.map(instructor => [instructor.id, instructor.workSchedule || null])));
      return;
    }
    const activeInstructors = (Array.isArray(people) ? people : []).filter(person => {
      if (roles && roles.error) {
        return person.roleId === 3 && person.status === 'active';
      }
      let rolesList = [];
      if (Array.isArray(roles)) {
        rolesList = roles;
      } else if (roles && typeof roles === 'object') {
        rolesList = Object.values(roles);
      }
      const isInstructor = rolesList.find(r => r.id === person.roleId)?.name === 'instructor';
      const isActive = person.status === 'active';
      
      return isInstructor && isActive;
    });
    
    const schedules = {};
    
    for (const instructor of activeInstructors) {
      try {
        const headers = currentUser?.sessionId ? { 'session-id': currentUser.sessionId } : {};
        const response = await fetch(`${API_BASE_URL}/work-schedule/${instructor.id}${selectedLocationId ? `?locationId=${encodeURIComponent(selectedLocationId)}` : ''}`, { headers });
        if (response.ok) {
          const data = await response.json();
          schedules[instructor.id] = data;
        }
      } catch (error) {
      }
    }
    setWorkSchedules(schedules);
  };

  React.useEffect(() => {
    if (people && roles) {
      fetchWorkSchedules();
    }
  }, [people, roles, selectedLocationId, locationResources]);

  React.useEffect(() => {
    const { startDate, endDate } = getDateRange();
    setIsLoadingReservations(true);
    fetchReservations(startDate, endDate);
  }, [date, view, fetchReservations]);

  React.useEffect(() => {
    setIsLoadingReservations(false);
  }, [reservations]);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    endDate: '',
    endTime: '',
    instructor: '',
    aircraft: '',
    student: '',
    safetyPilotId: '',
    renterChargePercentage: '70',
    type: '',
    comments: ''
  });
  const selectedReservationType = findReservationType(reservationTypes, formData);
  const showReservationManeuvers = !!formData.instructor;
  React.useEffect(() => {
    if (showReservationForm) fetchReservationTypes?.();
  }, [showReservationForm, fetchReservationTypes]);
  React.useEffect(() => {
    if (showReservationForm && !editingReservation && !formData.endTime && selectedReservationType?.duration != null) {
      setFormData(previous => applyReservationDuration(previous, selectedReservationType));
    }
  }, [showReservationForm, editingReservation, formData.endTime, formData.date, formData.time, selectedReservationType]);
  const [bookingAvailability, setBookingAvailability] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  React.useEffect(() => {
    let active = true;
    setBookingAvailability(null);
    setAvailabilityError('');
    setCheckingAvailability(false);
    if (!showReservationForm || !selectedLocationId || !formData.date || !formData.time || !formData.endTime || !currentUser?.sessionId) return;
    const startDateTime = schoolWallClockToIso(formData.date, formData.time);
    const endDateTime = schoolWallClockToIso(formData.endDate || formData.date, formData.endTime);
    if (!startDateTime || !endDateTime || Date.parse(endDateTime) <= Date.parse(startDateTime)) return;
    setCheckingAvailability(true);
    const query = new URLSearchParams({ locationId: String(editingReservation?.locationId || selectedLocationId), startDateTime, endDateTime });
    if (editingReservation?.id) query.set('reservationId', editingReservation.id);
    fetch(`${API_BASE_URL}/reservation/availability?${query}`, { headers: { 'session-id': currentUser.sessionId } })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to check availability');
        if (active) setBookingAvailability(data);
      }).catch(error => { if (active) setAvailabilityError(error.message); })
      .finally(() => { if (active) setCheckingAvailability(false); });
    return () => { active = false; };
  }, [showReservationForm, selectedLocationId, editingReservation?.id, editingReservation?.locationId, formData.date, formData.time, formData.endDate, formData.endTime, currentUser?.sessionId]);
  const [selectedManeuvers, setSelectedManeuvers] = useState([]);
  const [activeManeuverCategory, setActiveManeuverCategory] = useState('');
  const [hoveredManeuver, setHoveredManeuver] = useState(null);
  const [selectedManeuverDetails, setSelectedManeuverDetails] = useState(null);
  const [maneuverPopupPosition, setManeuverPopupPosition] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('basic');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showManeuverSelectionConfirmModal, setShowManeuverSelectionConfirmModal] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleCustomReason, setRescheduleCustomReason] = useState('');
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);
  const originalReservationForm = React.useRef(null);
  const [draggedReservation, setDraggedReservation] = useState(null);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [originalPosition, setOriginalPosition] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [safetyPilotFilter, setSafetyPilotFilter] = useState('');
  const [showSafetyPilotDropdown, setShowSafetyPilotDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoadingReservations, setIsLoadingReservations] = useState(false);
  const dateInputRef = React.useRef(null);
  const maneuversPanelRef = React.useRef(null);
  const wasManeuversTabVisibleRef = React.useRef(false);
  const [aircraftData, setAircraftData] = useState(null);
  const [latestAircraftLogId, setLatestAircraftLogId] = useState(null);
  const [checkOutData, setCheckOutData] = useState({ endHobbs: '', endTach: '', remarks: '' });
  const [checkInData, setCheckInData] = useState({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' });
  const [showAircraftModal, setShowAircraftModal] = useState(false);
  const [selectedAircraftItem, setSelectedAircraftItem] = useState(null);
  const [selectedAircraftImageIndex, setSelectedAircraftImageIndex] = useState(0);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [selectedInstructorItem, setSelectedInstructorItem] = useState(null);
  const [enlargedScheduleImage, setEnlargedScheduleImage] = useState(null);
  const [selectedInstructorHoursLast24, setSelectedInstructorHoursLast24] = useState(null);
  const [isLoadingSelectedInstructorHoursLast24, setIsLoadingSelectedInstructorHoursLast24] = useState(false);
  const [checkOutMessage, setCheckOutMessage] = useState('');
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [lessonData, setLessonData] = useState({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '', referralCredit: '' });
  const [lessonMessage, setLessonMessage] = useState('');
  const [isSavingLesson, setIsSavingLesson] = useState(false);
  const [lessonId, setLessonId] = useState(null);
  const [lessonFeeOverride, setLessonFeeOverride] = useState(null);
  const [reservationFlightLogEntry, setReservationFlightLogEntry] = useState(null);
  const [flightLogMessage, setFlightLogMessage] = useState('');
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [isConfirmingSafetyPilot, setIsConfirmingSafetyPilot] = useState(false);
  const [isDenyingSafetyPilot, setIsDenyingSafetyPilot] = useState(false);
  const [reservationUsersById, setReservationUsersById] = useState({});
  const [aircraftMaintenanceData, setAircraftMaintenanceData] = useState(null);
  const [reservationSquawks, setReservationSquawks] = useState([]);
  const [isLoadingReservationSquawks, setIsLoadingReservationSquawks] = useState(false);
  const [reservationSquawkMessage, setReservationSquawkMessage] = useState('');
  const [newReservationSquawk, setNewReservationSquawk] = useState({ issue: '', details: '' });
  const [reservationLookupId, setReservationLookupId] = useState('');
  const [reservationLookupMessage, setReservationLookupMessage] = useState('');
  const [reservationLookupMessageType, setReservationLookupMessageType] = useState('');

  const sanitizeManeuverHtml = (rawHtml) => {
    if (!rawHtml || typeof rawHtml !== 'string') return '';
    return rawHtml
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
      .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
      .replace(/\sjavascript:/gi, ' ')
      .trim();
  };

  const getManeuverStepsContent = (maneuver) => {
    if (!maneuver || !maneuver.steps) {
      return { type: 'none', lines: [], html: '' };
    }

    if (Array.isArray(maneuver.steps)) {
      const lines = maneuver.steps.map(step => `${step || ''}`.trim()).filter(Boolean);
      return { type: lines.length ? 'text' : 'none', lines, html: '' };
    }

    if (typeof maneuver.steps === 'string') {
      const stepsText = maneuver.steps.trim();
      const containsHtml = /<\/?[a-z][\s\S]*>/i.test(stepsText);
      if (containsHtml) {
        const html = sanitizeManeuverHtml(stepsText);
        return { type: html ? 'html' : 'none', lines: [], html };
      }
      const lines = stepsText.split(/\r?\n/).map(step => step.trim()).filter(Boolean);
      return { type: lines.length ? 'text' : 'none', lines, html: '' };
    }

    return { type: 'none', lines: [], html: '' };
  };

  const handleManeuverMouseMove = (event, maneuver) => {
    const stepContent = getManeuverStepsContent(maneuver);
    if (stepContent.type === 'none') {
      setHoveredManeuver(null);
      return;
    }
    setHoveredManeuver(maneuver);
    setManeuverPopupPosition({ x: event.clientX, y: event.clientY });
  };

  const getTachBackgroundColor = (tachValue) => {
    if (!aircraftMaintenanceData || !tachValue || tachValue === '') return 'white';
    const tach = parseFloat(tachValue);
    if (isNaN(tach)) return 'white';
    
    const { nextOilChange, nextHundredHour } = aircraftMaintenanceData;
    
    const lastEvents = [];
    if (nextOilChange && nextOilChange > 0) lastEvents.push(nextOilChange - 50);
    if (nextHundredHour && nextHundredHour > 0) lastEvents.push(nextHundredHour - 100);
    if (!lastEvents.length) return 'white';
    const effectiveNextOil = Math.max(...lastEvents) + 50;
    const minDiff = effectiveNextOil - tach;
    
    if (minDiff === Infinity) return 'white';
    if (minDiff < 10) return 'orange';
    if (minDiff < 15) return 'yellow';
    return 'white';
  };

  const hoveredManeuverStepsContent = getManeuverStepsContent(hoveredManeuver);
  const selectedManeuverStepsContent = getManeuverStepsContent(selectedManeuverDetails);
  const getFirstManeuverCategory = React.useCallback(() => {
    const categories = [...new Set((Array.isArray(maneuvers) ? maneuvers : []).map(m => m.category || 'Maneuvers'))].sort();
    return categories[0] || '';
  }, [maneuvers]);
  const maneuversPanelWidth = maneuversPanelRef.current?.getBoundingClientRect().width;
  const maneuverPopupWidth = (() => {
    if (typeof window === 'undefined') return maneuversPanelWidth || 700;
    const preferredWidth = maneuversPanelWidth || 700;
    return Math.min(preferredWidth, window.innerWidth - 12);
  })();
  const maneuverPopupLeft = (() => {
    if (typeof window === 'undefined') return maneuverPopupPosition.x;
    const maxLeft = Math.max(0, window.innerWidth - maneuverPopupWidth - 6);
    return Math.max(0, Math.min(maneuverPopupPosition.x, maxLeft));
  })();

  // Auto-fetch aircraft data when lesson or aircraft tab is active
  React.useEffect(() => {
    if ((activeTab === 'lesson' || activeTab === 'aircraft') && editingReservation && editingReservation.aircraftId && !aircraftMaintenanceData) {
      fetchAircraftLatestLog(editingReservation.aircraftId);
    }
  }, [activeTab, editingReservation, aircraftMaintenanceData]);

  React.useEffect(() => {
    if (activeTab !== 'aircraft' || !editingReservation?.aircraftId) {
      return;
    }

    if (latestAircraftLogId) {
      fetchReservationSquawks({ aircraftLogId: latestAircraftLogId, aircraftId: editingReservation.aircraftId });
    } else {
      fetchReservationSquawks({ aircraftId: editingReservation.aircraftId });
    }
  }, [activeTab, latestAircraftLogId, editingReservation?.aircraftId]);

  React.useEffect(() => {
    if (!showReservationForm) {
      setSelectedManeuverDetails(null);
    }
  }, [showReservationForm]);

  React.useEffect(() => {
    const isManeuversTabVisible = showReservationForm && !editingReservation && showReservationManeuvers;

    if (isManeuversTabVisible && !wasManeuversTabVisibleRef.current) {
      setActiveManeuverCategory(getFirstManeuverCategory());
    }

    wasManeuversTabVisibleRef.current = isManeuversTabVisible;
    if (!showReservationManeuvers && activeTab === 'maneuvers') setActiveTab('basic');
  }, [showReservationForm, editingReservation, showReservationManeuvers, activeTab, getFirstManeuverCategory]);

  // Force re-render when aircraft maintenance data is loaded
  React.useEffect(() => {
    // This effect ensures the background colors update when maintenance data is loaded
  }, [aircraftMaintenanceData, checkOutData.endTach, checkInData.endTach]);

  const getDisplayName = React.useCallback((user) => {
    if (!user) return '';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user.username || user.email || `User ${user.id}`;
  }, []);

  const hasEligibleSafetyPilotCertification = React.useCallback((user) => {
    const certificationText = `${user?.certifications || ''}`.toUpperCase();
    return certificationText.includes('PPL')
      || certificationText.includes('CPL')
      || certificationText.includes('ATP')
      || certificationText.includes('PRIVATE PILOT')
      || certificationText.includes('COMMERCIAL PILOT');
  }, []);

  const loadAvailableUsers = React.useCallback(async ({ startDateTime, endDateTime, excludeUserId } = {}) => {
    if (!currentUser?.sessionId) {
      setAvailableUsers([]);
      return;
    }

    try {
      const query = new URLSearchParams();
      if (selectedLocationId) query.set('locationId', selectedLocationId);
      if (editingReservation?.id) query.set('reservationId', editingReservation.id);
      if (startDateTime) query.append('startDateTime', startDateTime);
      if (endDateTime) query.append('endDateTime', endDateTime);
      if (excludeUserId != null) query.append('excludeUserId', `${excludeUserId}`);

      const endpoint = `${API_BASE_URL}/user/list/available${query.toString() ? `?${query.toString()}` : ''}`;
      const response = await fetch(endpoint, {
        headers: {
          'session-id': currentUser.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(Array.isArray(data) ? data : []);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      setAvailableUsers([]);
    }
  }, [currentUser?.sessionId, selectedLocationId, editingReservation?.id]);

  React.useEffect(() => {
    if (showReservationForm && formData.type === 'Rental') {
      const selectedClientId = (Array.isArray(people) ? people : []).find(
        (person) => `${person.firstName || person.first_name || ''} ${person.lastName || person.last_name || ''}`.trim() === formData.student
      )?.id;

      const startDateTime = formData.date && formData.time ? `${formData.date}T${formData.time}:00` : undefined;
      const endDateTime = formData.endDate && formData.endTime ? `${formData.endDate}T${formData.endTime}:00` : undefined;
      const excludeUserId = selectedClientId || editingReservation?.userIdClient;

      loadAvailableUsers({ startDateTime, endDateTime, excludeUserId });
    }
  }, [showReservationForm, formData.type, formData.date, formData.time, formData.endDate, formData.endTime, formData.student, people, editingReservation?.userIdClient, loadAvailableUsers]);

  const canClientAccessReservation = React.useCallback((reservation) => {
    if (currentUser?.user?.orgRole !== 'client') {
      return true;
    }

    const currentUserId = currentUser?.user?.id;
    return Number(reservation?.userIdClient) === Number(currentUserId) || Number(reservation?.safetyPilotId) === Number(currentUserId);
  }, [currentUser?.user?.id, currentUser?.user?.orgRole]);

  const canViewClientNames = React.useMemo(() => {
    const role = currentUser?.user?.orgRole;
    return role === 'staff' || role === 'admin' || role === 'superuser';
  }, [currentUser?.user?.orgRole]);

  const canLookupReservationById = React.useMemo(() => {
    const role = currentUser?.user?.orgRole;
    return role === 'admin' || role === 'superuser';
  }, [currentUser?.user?.orgRole]);

  const isSelectedUserInstructor = React.useMemo(() => {
    const selectedUserRoleId = Number(selectedInstructorItem?.roleId ?? selectedInstructorItem?.role_id);
    return selectedUserRoleId === 3;
  }, [selectedInstructorItem]);

  const isFleetAircraftForCurrentOrg = React.useCallback((aircraftRecord) => {
    if (!aircraftRecord) return false;

    const aircraftId = Number(aircraftRecord.id);
    const ownerId = Number(aircraftRecord.ownerId);
    const orgAircraftCollections = [
      currentOrg?.aircraftIds,
      currentOrg?.fleetAircraftIds,
      currentOrg?.aircraft,
      currentOrg?.fleet,
      currentOrg?.aircraftList,
      currentOrg?.aircrafts,
      currentOrg?.ownedAircraftIds
    ].filter(Boolean);

    const matchesCollection = orgAircraftCollections.some((collection) => {
      if (!Array.isArray(collection)) return false;
      return collection.some((item) => {
        if (item == null) return false;
        if (typeof item === 'number' || typeof item === 'string') {
          return Number(item) === aircraftId;
        }
        return Number(item.id ?? item.aircraftId ?? item.value) === aircraftId;
      });
    });

    if (matchesCollection) return true;

    const orgOwnerIds = [
      currentOrg?.ownerId,
      currentOrg?.ownerUserId,
      currentOrg?.userId,
      currentOrg?.managerId,
      currentOrg?.createdByUserId,
      currentOrg?.createdBy
    ].filter((value) => value != null);

    if (orgOwnerIds.some((value) => Number(value) === ownerId)) {
      return true;
    }

    return false;
  }, [currentOrg]);

  const getSafetyPilotStatus = React.useCallback((reservation) => {
    const status = reservation?.safetyPilotStatus;
    if (status === 'confirmed' || status === 'denied' || status === 'unconfirmed') {
      return status;
    }
    return reservation?.safetyPilotId ? 'unconfirmed' : 'not-assigned';
  }, []);

  const isCurrentUserAssignedSafetyPilot = React.useCallback((reservation) => {
    if (!reservation?.safetyPilotId) return false;
    return Number(currentUser?.user?.id) === Number(reservation.safetyPilotId);
  }, [currentUser?.user?.id]);

  const canCurrentUserEditReservation = React.useCallback((reservation) => {
    if (!canClientAccessReservation(reservation)) {
      return false;
    }

    return !isCurrentUserAssignedSafetyPilot(reservation);
  }, [canClientAccessReservation, isCurrentUserAssignedSafetyPilot]);

  const canCurrentUserConfirmSafetyPilot = React.useCallback((reservation) => {
    if (!isCurrentUserAssignedSafetyPilot(reservation)) return false;
    const status = getSafetyPilotStatus(reservation);
    return status === 'unconfirmed' || status === 'denied';
  }, [getSafetyPilotStatus, isCurrentUserAssignedSafetyPilot]);

  const canCurrentUserDenySafetyPilot = React.useCallback((reservation) => {
    if (!isCurrentUserAssignedSafetyPilot(reservation)) return false;
    const status = getSafetyPilotStatus(reservation);
    return status === 'unconfirmed' || status === 'confirmed';
  }, [getSafetyPilotStatus, isCurrentUserAssignedSafetyPilot]);

  React.useEffect(() => {
    const currentUserId = Number(currentUser?.user?.id);
    if (!currentUser?.sessionId || !Number.isFinite(currentUserId)) {
      setReservationUsersById({});
      return;
    }

    const safetyPilotReservations = (Array.isArray(reservations) ? reservations : []).filter(
      (reservation) => reservation?.status === 'active' && Number(reservation?.safetyPilotId) === currentUserId
    );

    const requestedIds = [...new Set(safetyPilotReservations
      .map((reservation) => {
        const reservationId = Number(reservation?.id);
        return Number.isFinite(reservationId) ? reservationId : null;
      })
      .filter((id) => id !== null))];

    if (requestedIds.length === 0) {
      setReservationUsersById({});
      return;
    }

    let cancelled = false;
    const loadReservationUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user/list/reservations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'session-id': currentUser.sessionId
          },
          body: JSON.stringify({ ids: requestedIds })
        });

        if (!response.ok) {
          if (!cancelled) {
            setReservationUsersById({});
          }
          return;
        }

        const data = await response.json();
        const mappedUsers = {};
        (Array.isArray(data) ? data : []).forEach((user, index) => {
          const explicitReservationId = Number(user?.reservationId ?? user?.reservation_id);
          const mappedId = Number.isFinite(explicitReservationId) ? explicitReservationId : requestedIds[index];
          if (Number.isFinite(mappedId)) {
            mappedUsers[mappedId] = user;
          }
        });

        if (!cancelled) {
          setReservationUsersById(mappedUsers);
        }
      } catch (error) {
        if (!cancelled) {
          setReservationUsersById({});
        }
      }
    };

    loadReservationUsers();

    return () => {
      cancelled = true;
    };
  }, [reservations, currentUser?.sessionId, currentUser?.user?.id]);

  React.useEffect(() => {
    const canViewDualHours = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
    const instructorId = selectedInstructorItem?.id;

    if (!showInstructorModal || !canViewDualHours || !isSelectedUserInstructor || !currentUser?.sessionId || !instructorId) {
      setSelectedInstructorHoursLast24(null);
      setIsLoadingSelectedInstructorHoursLast24(false);
      return;
    }

    let cancelled = false;

    const loadSelectedInstructorHours = async () => {
      setIsLoadingSelectedInstructorHoursLast24(true);
      try {
        const response = await fetch(`${API_BASE_URL}/user/${instructorId}/lessons/last24/taught`, {
          headers: { 'session-id': currentUser.sessionId }
        });

        if (!cancelled) {
          if (response.ok) {
            const data = await response.json();
            const hours = Number(data?.hoursTaughtLast24);
            setSelectedInstructorHoursLast24(Number.isFinite(hours) ? hours : 0);
          } else {
            setSelectedInstructorHoursLast24(0);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedInstructorHoursLast24(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSelectedInstructorHoursLast24(false);
        }
      }
    };

    loadSelectedInstructorHours();

    return () => {
      cancelled = true;
    };
  }, [showInstructorModal, selectedInstructorItem?.id, currentUser?.sessionId, currentUser?.user?.orgRole, isSelectedUserInstructor]);

  const getClientNameForReservation = React.useCallback((reservation) => {
    const reservationId = Number(reservation?.id);
    const reservationClientId = Number(reservation?.userIdClient ?? reservation?.userId ?? reservation?.user_id);
    const personClient = (Array.isArray(people) ? people : []).find((person) => Number(person.id) === reservationClientId) || null;
    const mappedClient = Number.isFinite(reservationId) ? reservationUsersById[reservationId] : null;
    const embeddedClient = reservation?.client || null;

    const fromPeople = personClient ? `${personClient.firstName || personClient.first_name || ''} ${personClient.lastName || personClient.last_name || ''}`.trim() : '';
    const fromMapped = mappedClient ? `${mappedClient.firstName || mappedClient.first_name || ''} ${mappedClient.lastName || mappedClient.last_name || ''}`.trim() : '';
    const fromEmbedded = embeddedClient ? `${embeddedClient.firstName || embeddedClient.first_name || ''} ${embeddedClient.lastName || embeddedClient.last_name || ''}`.trim() : '';

    return fromPeople || fromMapped || fromEmbedded || reservation?.clientName || reservation?.client_name || reservation?.studentName || reservation?.student_name || '';
  }, [people, reservationUsersById]);

  const getReservationCreatedMeta = React.useCallback((reservation) => {
    const candidates = [
      reservation,
      reservation?.reservation,
      reservation?.data,
      reservation?.item,
      reservation?.attributes,
      reservation?.meta,
      reservation?.audit,
      reservation?.auditTrail
    ].filter(Boolean);

    const createdByUsername = candidates
      .map((item) => item?.createdByUsername
        || item?.created_by_username
        || item?.createdUserName
        || item?.created_user_name
        || item?.createdBy?.username
        || item?.created_by?.username
        || item?.createdByName
        || item?.created_by_name
        || item?.createdBy
        || item?.created_by
        || item?.createdByEmail
        || item?.created_by_email
        || item?.createdUser
        || item?.created_user
        || '')
      .find(Boolean) || '';

    const createdAtRaw = candidates
      .map((item) => item?.createdDatetime
        || item?.created_datetime
        || item?.createdAt
        || item?.created_at
        || item?.createdDateTime
        || item?.created_date_time
        || item?.createdOn
        || item?.created_on
        || item?.creationDatetime
        || item?.creation_datetime
        || item?.createDatetime
        || item?.create_datetime
        || item?.dateCreated
        || item?.date_created
        || item?.createdTimestamp
        || item?.created_timestamp
        || item?.timestampCreated
        || item?.timestamp_created
        || item?.created
        || '')
      .find(Boolean) || '';

    let createdAtText = '';
    if (createdAtRaw) {
      const createdAtDate = new Date(createdAtRaw);
      createdAtText = Number.isNaN(createdAtDate.getTime()) ? `${createdAtRaw}` : createdAtDate.toLocaleString();
    }

    return {
      createdBy: createdByUsername,
      createdAtText,
      title: `Created by username: ${createdByUsername || 'Unknown'}\nCreated datetime: ${createdAtText || 'Unknown'}`
    };
  }, []);

  const handleConfirmSafetyPilot = async () => {
    const reservationId = editingReservation?.id;
    if (!reservationId || isConfirmingSafetyPilot) {
      return;
    }

    const currentUserId = currentUser?.user?.id;
    if (!editingReservation?.safetyPilotId || Number(currentUserId) !== Number(editingReservation.safetyPilotId)) {
      setMessage('Error: Only the assigned safety pilot can confirm this reservation');
      setIsError(true);
      return;
    }

    if (!canCurrentUserConfirmSafetyPilot(editingReservation)) {
      setMessage('Error: Safety pilot can only confirm when status is unconfirmed or denied');
      setIsError(true);
      return;
    }

    setIsConfirmingSafetyPilot(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/safety-pilot/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ reservationId })
      });

      if (response.ok) {
        setEditingReservation((previous) => previous ? { ...previous, safetyPilotStatus: 'confirmed' } : previous);
        setMessage('Safety pilot confirmed successfully');
        setIsError(false);
        const { startDate, endDate } = getDateRange();
        fetchReservations(startDate, endDate);
      } else {
        const errorText = await response.text();
        setMessage(`Error: ${errorText || 'Failed to confirm safety pilot'}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setIsError(true);
    } finally {
      setIsConfirmingSafetyPilot(false);
    }
  };

  const handleDenySafetyPilot = async () => {
    const reservationId = editingReservation?.id;
    if (!reservationId || isDenyingSafetyPilot) {
      return;
    }

    const currentUserId = currentUser?.user?.id;
    if (!editingReservation?.safetyPilotId || Number(currentUserId) !== Number(editingReservation.safetyPilotId)) {
      setMessage('Error: Only the assigned safety pilot can deny this reservation');
      setIsError(true);
      return;
    }

    if (!canCurrentUserDenySafetyPilot(editingReservation)) {
      setMessage('Error: Safety pilot can only deny when status is unconfirmed or confirmed');
      setIsError(true);
      return;
    }

    setIsDenyingSafetyPilot(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/safety-pilot/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ reservationId })
      });

      if (response.ok) {
        const { startDate, endDate } = getDateRange();
        fetchReservations(startDate, endDate);

        setShowReservationForm(false);
        setEditingReservation(null);
        setSelectedManeuvers([]);
        setActiveTab('basic');
        setClientFilter('');
        setSafetyPilotFilter('');
        setShowSafetyPilotDropdown(false);
        setMessage('');
        setIsSubmitting(false);
        setShowManeuverSelectionConfirmModal(false);
        setAircraftData(null);
        setAircraftMaintenanceData(null);
        setCheckOutData({ endHobbs: '', endTach: '', remarks: '' });
        setCheckInData({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' });
        setCheckOutMessage('');
        setIsCheckedOut(false);
        setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '' });
        setLessonMessage('');
        setLessonId(null);
        setReservationFlightLogEntry(null);
        setFlightLogMessage('');
        setReservationSquawks([]);
        setReservationSquawkMessage('');
        setNewReservationSquawk({ issue: '', details: '' });
      } else {
        const errorText = await response.text();
        setMessage(`Error: ${errorText || 'Failed to deny safety pilot request'}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setIsError(true);
    } finally {
      setIsDenyingSafetyPilot(false);
    }
  };

  const handleSubmit = async (e, skipManeuverSelectionConfirm = false) => {
    if (e) {
      e.preventDefault();
    }
    if (isSubmitting) return;

    if (editingReservation && isCurrentUserAssignedSafetyPilot(editingReservation)) {
      setMessage('Error: Safety pilot cannot edit this reservation. You can only confirm or deny.');
      setIsError(true);
      return;
    }
    
    if (!formData.instructor && !formData.aircraft) {
      setMessage('Error: Please select at least an instructor or an aircraft');
      setIsError(true);
      return;
    }
    
    if (formData.type === 'Flight Training' && !formData.instructor) {
      setMessage('Error: Instructor is required for Flight Training');
      setIsError(true);
      return;
    }
    
    if (formData.type === 'Discovery' && !formData.instructor) {
      setMessage('Error: Instructor is required for Discovery Flight');
      setIsError(true);
      return;
    }
    
    if (formData.type === 'Discovery' && !formData.aircraft) {
      setMessage('Error: Aircraft is required for Discovery Flight');
      setIsError(true);
      return;
    }
    
    if (formData.type === 'Ground lesson' && !formData.instructor) {
      setMessage('Error: Instructor is required for Ground Training');
      setIsError(true);
      return;
    }
    
    if (formData.type === 'Ground lesson' && formData.aircraft) {
      setMessage('Error: Aircraft must not be selected for Ground Training');
      setIsError(true);
      return;
    }

    const instructorId = (Array.isArray(instructors) ? instructors : []).find(i => `${i.firstName} ${i.lastName}` === formData.instructor)?.id || null;
    const clientId = (Array.isArray(people) ? people : []).find(p => `${p.firstName || p.first_name} ${p.lastName || p.last_name}`.trim() === formData.student)?.id || null;
    if (!editingReservation && (loadingReservationTypes || reservationTypesError || !selectedReservationType)) {
      setMessage(reservationTypesError || 'Select an organization reservation type before saving.');
      setIsError(true);
      return;
    }
    if (selectedLocationId) {
      const aircraftError = aircraftLocationError(formData.aircraft,
        new Date(schoolWallClockToIso(formData.date, formData.time)),
        new Date(schoolWallClockToIso(formData.endDate || formData.date, formData.endTime)),
        editingReservation?.locationId || selectedLocationId);
      if (aircraftError) { setMessage(aircraftError); setIsError(true); return; }
      if (checkingAvailability || !bookingAvailability) {
        setMessage(availabilityError || 'Wait for the location availability check before saving.');
        setIsError(true);
        return;
      }
      const aircraftAvailable = !formData.aircraft || (bookingAvailability.aircraft || []).some(item => Number(item.id) === Number(formData.aircraft));
      const instructorAvailable = !instructorId || (bookingAvailability.instructors || []).some(item => Number(item.id) === Number(instructorId));
      const canSelfSchedule = selectedLocation?.isDefault && isCurrentUserInstructor(instructorId) && clientId != null;
      if (!aircraftAvailable || (!instructorAvailable && !canSelfSchedule)) {
        setMessage('The selected resources are unavailable at this location during the requested time.');
        setIsError(true);
        return;
      }
    }
    const hasAvailableManeuvers = (Array.isArray(maneuvers) ? maneuvers : []).some(maneuver =>
      maneuver && `${maneuver.name || ''}`.trim().length > 0
    );
    const shouldWarnNoManeuversSelected =
      !skipManeuverSelectionConfirm &&
      showReservationManeuvers &&
      !!formData.instructor &&
      hasAvailableManeuvers &&
      selectedManeuvers.length === 0;

    if (shouldWarnNoManeuversSelected) {
      setShowManeuverSelectionConfirmModal(true);
      return;
    }
    
    // Check if instructor is available for the entire reservation period
    if (formData.instructor) {
      const instructor = (Array.isArray(instructors) ? instructors : []).find(i => `${i.firstName} ${i.lastName}` === formData.instructor);
      
      if (instructor) {
        // Fix date parsing issue - add time to prevent timezone problems
        const reservationDate = new Date(formData.date + 'T12:00:00');
        const [startHours, startMinutes] = formData.time.split(':').map(Number);
        const [endHours, endMinutes] = formData.endTime.split(':').map(Number);
        
        // Check every 30-minute slot in the reservation period
        let currentTime = startHours * 60 + startMinutes;
        const endTime = endHours * 60 + endMinutes;
        
        while (currentTime < endTime) {
          const hours = Math.floor(currentTime / 60);
          const minutes = currentTime % 60;
          const timeSlot = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          
          const isAvailable = isTimeSlotAvailable(instructor, timeSlot, reservationDate);
          const overrideStatus = getOverrideStatus(instructor, timeSlot, reservationDate);
          const finalAvailable = (isAvailable || overrideStatus === 'on') && overrideStatus !== 'off';
          const canSelfScheduleWithClient = isCurrentUserInstructor(instructor.id)
            && clientId != null;
          
          if (!finalAvailable && !canSelfScheduleWithClient) {
            setMessage(`Error: Instructor is not available at ${timeSlot}`);
            setIsError(true);
            return;
          }
          
          currentTime += 30;
        }
      }
    }

    if (checkTimeConflict(
      formData.date,
      formData.time,
      formData.endDate || formData.date,
      formData.endTime,
      editingReservation?.id,
      formData.aircraft,
      instructorId
    )) {
      setMessage('Error: The selected aircraft or instructor is already reserved during this time');
      setIsError(true);
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    setIsError(false);
    
    const convertTimeToMilitary = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 100 + minutes;
    };
    
    const safetyPilotId = formData.type === 'Rental' && formData.safetyPilotId ? parseInt(formData.safetyPilotId, 10) : null;
    const parsedRenterChargePercentage = formData.renterChargePercentage === '' ? 70 : parseFloat(formData.renterChargePercentage);
    const renterChargePercentage = Number.isFinite(parsedRenterChargePercentage)
      ? Math.max(0, Math.min(100, parsedRenterChargePercentage))
      : 70;

    if (formData.type === 'Rental' && safetyPilotId && clientId && safetyPilotId === clientId) {
      setMessage('Error: Safety pilot must be different from client');
      setIsError(true);
      setIsSubmitting(false);
      return;
    }

    if (formData.type === 'Rental' && safetyPilotId) {
      const selectedSafetyPilot = (Array.isArray(people) ? people : []).find(
        (person) => Number(person.id) === Number(safetyPilotId)
      );
      if (!hasEligibleSafetyPilotCertification(selectedSafetyPilot)) {
        setMessage('Error: Safety pilot must have PPL, CPL, or ATP certification');
        setIsError(true);
        setIsSubmitting(false);
        return;
      }
    }
    
    const payload = {
      ...(selectedLocationId ? { locationId: Number(editingReservation?.locationId || selectedLocationId) } : {}),
      startDate: formData.date,
      startTime: convertTimeToMilitary(formData.time),
      endDate: formData.endDate,
      endTime: convertTimeToMilitary(formData.endTime),
      userIdInstructor: instructorId,
      aircraftId: formData.aircraft || null,
      userIdClient: clientId,
      safetyPilotId,
      renterChargePercentage: safetyPilotId ? renterChargePercentage : null,
      comment: formData.comments,
      type: formData.type,
      reservationTypeId: selectedReservationType?.id ?? null,
      status: 'active'
    };
    
    if (showReservationManeuvers && instructorId && selectedManeuvers.length > 0) {
      payload.maneuvers = selectedManeuvers.map(id => ({ id }));
    }
    
    if (editingReservation) {
      payload.id = editingReservation.id;
      const originalForm = originalReservationForm.current || {};
      const detailsChanged = Object.keys({ ...originalForm, ...formData }).some(key => String(originalForm[key] ?? '') !== String(formData[key] ?? ''));
      const maneuversChanged = JSON.stringify(selectedManeuvers.map(String).sort()) !== JSON.stringify((editingReservation.maneuvers || []).map(item => String(item.id)).sort());
      if (requiresReservationReason(editingReservation) && (detailsChanged || maneuversChanged)) {
        setPendingSubmitPayload(payload);
        setRescheduleReason('');
        setRescheduleCustomReason('');
        setIsSubmitting(false);
        setShowRescheduleModal(true);
        return;
      }
      if (rescheduleReason) {
        payload.rescheduleReason = rescheduleReason === 'other' ? rescheduleCustomReason : rescheduleReason;
        setRescheduleReason('');
        setRescheduleCustomReason('');
      }
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const { startDate, endDate } = getDateRange();
        fetchReservations(startDate, endDate);
        setMessage(editingReservation ? 'Reservation Updated Successfully' : 'Reservation Created Successfully');
        setIsError(false);
        setTimeout(() => {
          setShowReservationForm(false);
          setEditingReservation(null);
          setFormData({ date: '', time: '', endDate: '', endTime: '', instructor: '', aircraft: '', student: '', safetyPilotId: '', renterChargePercentage: '70', type: '', comments: '' });
          setSelectedManeuvers([]);
          setActiveTab('basic');
          setClientFilter('');
          setSafetyPilotFilter('');
          setMessage('');
        }, 2000);
      } else {
        const errorText = await response.text();
        let errorMessage = `Failed to ${editingReservation ? 'update' : 'create'} reservation`;
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        setMessage(`Error: ${errorMessage}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage(`Error: Error ${editingReservation ? 'updating' : 'creating'} reservation`);
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isReservationVisibleInCalendar = (reservation) => {
    const status = `${reservation?.status || ''}`.toLowerCase();
    return status !== 'cancelled' && status !== 'canceled' && status !== 'inactive';
  };

  const getReservationDayString = (rawDate) => {
    if (!rawDate) return '';
    if (typeof rawDate === 'string') {
      const leadingDateMatch = rawDate.match(/^(\d{4}-\d{2}-\d{2})/);
      if (leadingDateMatch) {
        return leadingDateMatch[1];
      }
      if (rawDate.includes('T')) return rawDate.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) return rawDate;
    }

    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return getDateString(parsedDate);
  };

  const toMilitaryNumber = (timeValue) => {
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      const [hoursText, minutesText] = timeValue.split(':');
      const parsedHours = Number(hoursText);
      const parsedMinutes = Number(minutesText);
      if (Number.isFinite(parsedHours) && Number.isFinite(parsedMinutes)) {
        return parsedHours * 100 + parsedMinutes;
      }
    }

    const numericTime = Number(timeValue);
    return Number.isFinite(numericTime) ? Math.trunc(numericTime) : 0;
  };

  const convertMilitaryToTime = (militaryTime) => {
    const normalizedTime = toMilitaryNumber(militaryTime);
    const hours = Math.floor(normalizedTime / 100);
    const minutes = normalizedTime % 100;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const isReservationOnDate = (reservation, targetDate) => {
    const targetDateStr = getDateString(targetDate);
    const startDateStr = getReservationDayString(
      reservation?.startDate ||
      reservation?.start_date ||
      reservation?.startDatetime ||
      reservation?.start_datetime ||
      reservation?.date ||
      reservation?.reservationDate ||
      reservation?.reservation_date
    );
    const endDateStr = getReservationDayString(
      reservation?.endDate ||
      reservation?.end_date ||
      reservation?.endDatetime ||
      reservation?.end_datetime
    ) || startDateStr;

    if (startDateStr && targetDateStr >= startDateStr && targetDateStr <= endDateStr) {
      return true;
    }

    // Fallback for inconsistent payload shapes: inspect every date-like field.
    const reservationEntries = Object.entries(reservation || {});
    for (const [key, value] of reservationEntries) {
      if (!key || typeof key !== 'string') continue;
      if (!key.toLowerCase().includes('date')) continue;
      const valueDateStr = getReservationDayString(value);
      if (valueDateStr && valueDateStr === targetDateStr) {
        return true;
      }
    }

    return false;
  };

  const getReservationsForDate = (date) => {
    return reservations.filter((r) => isReservationOnDate(r, date) && isReservationVisibleInCalendar(r));
  };

  const sortReservationsByStartTime = (list) => {
    return [...list].sort((a, b) => toMilitaryNumber(a?.startTime) - toMilitaryNumber(b?.startTime));
  };

  const getReservationSortKey = (reservation) => {
    return `${toMilitaryNumber(reservation?.startTime)}-${toMilitaryNumber(reservation?.endTime)}-${reservation?.id || ''}`;
  };

  const getReservationColor = (reservationType) => {
    switch (reservationType) {
      case 'Flight Training': return '#e8f5e8';
      case 'Rental': return '#fff3cd';
      case 'Solo': return '#e1ecf4';
      case 'Ground lesson': return '#f8d7da';
      case 'Checkride': return '#f3e5f5';
      default: return '#e3f2fd';
    }
  };

  const getReservationDisplay = (reservation, viewType) => {
    const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === reservation.aircraftId);
    const instructor = (Array.isArray(people) ? people : []).find(p => p.id === reservation.userIdInstructor);
    const safetyPilot = (Array.isArray(people) ? people : []).find(p => p.id === reservation.safetyPilotId) || reservation.safetyPilot || null;

    const convertToAmPm = (timeValue) => {
      const militaryTime = toMilitaryNumber(timeValue);
      const hours = Math.floor(militaryTime / 100);
      const minutes = militaryTime % 100;
      const h12 = hours == 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const ampm = hours < 12 ? 'A' : 'P';
      return `${h12}:${minutes.toString().padStart(2, '0')}${ampm}`;
    };

    const timeRange = `${convertToAmPm(reservation.startTime)}-${convertToAmPm(reservation.endTime)}`;
    const instructorName = instructor ? (instructor.firstName || instructor.first_name) : '';
    const clientName = getClientNameForReservation(reservation);
    const safetyPilotName = safetyPilot ? getDisplayName(safetyPilot) : '';
    const aircraftInfo = reservationAircraft ? `${reservationAircraft.tailNumber || reservationAircraft.registration || ''}` : '';

    const rows = [];
    rows.push({ label: 'Time:', value: timeRange });
    if (clientName && canViewClientNames) rows.push({ label: 'Client:', value: clientName });
    if (clientName && !canViewClientNames) rows.push({ label: 'Client:', value: 'Private' });
    if (instructorName) rows.push({ label: 'Instructor:', value: instructorName });
    if (safetyPilotName) rows.push({ label: 'Safety Pilot:', value: safetyPilotName, muted: reservation.safetyPilotStatus === 'unconfirmed' });
    if (aircraftInfo) rows.push({ label: 'Aircraft:', value: aircraftInfo });

    if (viewType === 'month') {
      const compactClient = canViewClientNames ? (clientName || 'Client') : 'Private';
      return (
        <div style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
          <div style={{ fontWeight: '600' }}>{timeRange}</div>
          <div>{compactClient}</div>
        </div>
      );
    }

    if (viewType === 'week') {
      const compactClient = canViewClientNames ? (clientName || 'Client') : 'Private';
      return (
        <div style={{ fontSize: '0.72rem', lineHeight: 1.25 }}>
          <div><strong>{timeRange}</strong></div>
          <div>{compactClient}</div>
          {instructorName && <div>Instr: {instructorName}</div>}
          {aircraftInfo && <div>AC: {aircraftInfo}</div>}
          {safetyPilotName && <div style={{ color: reservation.safetyPilotStatus === 'unconfirmed' ? '#8a8a8a' : 'inherit' }}>SP: {safetyPilotName}</div>}
        </div>
      );
    }

    return (
      <table style={{ width: '100%', fontSize: 'inherit' }}>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td style={{ fontWeight: 'bold', paddingRight: '4px', verticalAlign: 'top' }}>{row.label}</td>
              <td style={{ verticalAlign: 'top', color: row.muted ? '#8a8a8a' : 'inherit' }}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());

    const dates = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      dates.push(day);
    }
    return dates;
  };

  const getSlotInstant = (timeSlot, selectedDate) => {
    const instant = new Date(selectedDate);
    const [hours, minutes] = timeSlot.split(':').map(Number);
    instant.setHours(hours, minutes, 0, 0);
    return instant;
  };
  const getInstructorSlotWallTime = (timeSlot, selectedDate) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);
    return new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), hours, minutes));
  };
  const isTimeSlotAvailable = (instructor, timeSlot, selectedDate) => isLocationScheduleAvailable(
    instructor.workSchedule || workSchedules[instructor.id], getInstructorSlotWallTime(timeSlot, selectedDate), !!selectedLocation?.isDefault, true
  );
  const getOverrideStatus = (instructor, timeSlot, selectedDate) => getLocationOverride(
    instructor.workSchedule || workSchedules[instructor.id], getInstructorSlotWallTime(timeSlot, selectedDate), true
  );

  const isCurrentUserInstructor = (instructorId) => {
    const user = currentUser?.user;
    const isInstructor = user?.role?.toLowerCase() === 'instructor'
      || Number(user?.roleId) === 3
      || user?.orgRole === 'instructor';
    const currentUserId = user?.id ?? currentUser?.id;
    return isInstructor && currentUserId != null && Number(instructorId) === Number(currentUserId);
  };

  const openReservationForm = (selectedDate, instructor = '', aircraft = '', time = '') => {
    let startTime = time;
    if (!time) {
      // If no time provided, default to current hour
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes() >= 30 ? 30 : 0;
      startTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    }
    
    // Auto-select client if user is a client
    const clientName = currentUser?.user?.orgRole === 'client' ? 
      `${currentUser?.user?.firstName || currentUser?.firstName || ''} ${currentUser?.user?.lastName || currentUser?.lastName || ''}`.trim() : '';
    
    setEditingReservation(null);
    setFormData({
      date: getDateString(selectedDate),
      time: startTime,
      endDate: getDateString(selectedDate),
      endTime: '',
      instructor,
      aircraft,
      student: clientName,
      safetyPilotId: '',
      renterChargePercentage: '70',
      type: '',
      comments: ''
    });
    setSelectedManeuvers([]);
    setActiveManeuverCategory('');
    setActiveTab('basic');
    setShowReservationForm(true);
  };

  React.useEffect(() => {
    const requested = location.state?.assistantScheduler;
    if (!requested || assistantRequestRef.current === location.key) return;
    const finish = (error = '') => {
      assistantRequestRef.current = location.key;
      setAssistantSchedulerError(error);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    };
    const action = getAssistantSchedulerAction(requested);
    if (!action) { finish('The assistant returned an invalid scheduler action.'); return; }
    if (!locations.length && !locationError) return;
    if (action.locationId != null) {
      if (!locations.some(place => Number(place.id) === action.locationId)) { finish('The requested scheduling location is unavailable.'); return; }
      if (String(selectedLocationId) !== String(action.locationId)) {
        setSelectedLocationId(String(action.locationId));
        return;
      }
    }
    if (selectedLocationId && !locationResourcesReady) {
      if (locationError) finish(locationError);
      return;
    }
    if (showReservationForm) { finish('Close the current reservation before opening the assistant suggestion.'); return; }
    const selectedDate = new Date(`${action.preferredDate}T12:00:00`);
    const startHour = Math.max(0, Math.min(21, Number(settings?.flightReservationDayStart ?? 9)));
    setDate(selectedDate);
    setView('day');
    openReservationForm(selectedDate, '', '', `${String(startHour).padStart(2, '0')}:00`);
    setFormData(previous => ({ ...previous, type: action.flightType === 'lesson' ? 'Flight Training' : 'Solo' }));
    finish();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state, locations, locationError, selectedLocationId, locationResourcesReady, settings?.flightReservationDayStart, showReservationForm, setSelectedLocationId, navigate]);

  const calculateLessonTotal = () => {
    if (lessonFeeOverride !== null) return lessonFeeOverride;
    const selectedAircraftId = `${formData.aircraft || ''}`.trim();
    const lessonAircraftId = selectedAircraftId ? Number(selectedAircraftId) : null;
    const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find((item) => Number(item.id) === lessonAircraftId);
    const shouldApplyInstructionOwnerAircraftFee = selectedAircraftId.length === 0;
    const selectedInstructorId = (Array.isArray(instructors) ? instructors : []).find((instructor) => {
      const displayName = `${instructor.firstName || instructor.first_name || ''} ${instructor.lastName || instructor.last_name || ''}`.trim();
      return displayName === formData.instructor || instructor.username === formData.instructor;
    })?.id;
    const instructorId = Number(selectedInstructorId ?? editingReservation?.userIdInstructor);
    const instructor = (Array.isArray(people) ? people : []).find((person) => Number(person.id) === instructorId);
    const instructorRate = getBookedRate('instructor', instructorId, (instructors.find(item => Number(item.id) === instructorId)?.rate ?? instructor?.rate));
    const flightTime = lessonData.flightTime
      ? parseFloat(lessonData.flightTime)
      : (reservationAircraft && aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd && aircraftData.hobbsStart
          ? parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)
          : 0);
    const flightCharge = reservationAircraft && flightTime ? (getBookedRate('aircraft', lessonAircraftId, reservationAircraft.rate) * flightTime) : 0;
    const dualHours = lessonData.dualReceived ? parseFloat(lessonData.dualReceived) : 0;
    const dualBaseTotal = dualHours * instructorRate;
    const dualOwnerFeeTotal = shouldApplyInstructionOwnerAircraftFee ? (dualHours * instructionOwnerAircraftFee) : 0;
    const groundHours = lessonData.ground ? parseFloat(lessonData.ground) : 0;
    const purchases = lessonData.purchases ? parseFloat(lessonData.purchases) : 0;
    const referralCredit = lessonData.referralCredit ? parseFloat(lessonData.referralCredit) : 0;
    const total = flightCharge + dualBaseTotal + dualOwnerFeeTotal + (groundHours * instructorRate) + purchases - referralCredit;
    const feeStr = total.toFixed(2);
    
    setLessonData({...lessonData, fee: feeStr});
    return feeStr;
  };

  const computeLessonTotalFrom = (ldata) => {
    const selectedAircraftId = `${formData.aircraft || ''}`.trim();
    const lessonAircraftId = selectedAircraftId ? Number(selectedAircraftId) : null;
    const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find((item) => Number(item.id) === lessonAircraftId);
    const shouldApplyInstructionOwnerAircraftFee = selectedAircraftId.length === 0;
    const selectedInstructorId = (Array.isArray(instructors) ? instructors : []).find((instructor) => {
      const displayName = `${instructor.firstName || instructor.first_name || ''} ${instructor.lastName || instructor.last_name || ''}`.trim();
      return displayName === formData.instructor || instructor.username === formData.instructor;
    })?.id;
    const instructorId = Number(selectedInstructorId ?? editingReservation?.userIdInstructor);
    const instructor = (Array.isArray(people) ? people : []).find((person) => Number(person.id) === instructorId);
    const instructorRate = getBookedRate('instructor', instructorId, (instructors.find(item => Number(item.id) === instructorId)?.rate ?? instructor?.rate));
    const flightTime = ldata.flightTime
      ? parseFloat(ldata.flightTime)
      : (reservationAircraft && aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd && aircraftData.hobbsStart
          ? parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)
          : 0);
    const flightCharge = reservationAircraft && flightTime ? (getBookedRate('aircraft', lessonAircraftId, reservationAircraft.rate) * flightTime) : 0;
    const dualHours = ldata.dualReceived ? parseFloat(ldata.dualReceived) : 0;
    const dualBaseTotal = dualHours * instructorRate;
    const dualOwnerFeeTotal = shouldApplyInstructionOwnerAircraftFee ? (dualHours * instructionOwnerAircraftFee) : 0;
    const groundHours = ldata.ground ? parseFloat(ldata.ground) : 0;
    const purchases = ldata.purchases ? parseFloat(ldata.purchases) : 0;
    const referralCredit = ldata.referralCredit ? parseFloat(ldata.referralCredit) : 0;
    const total = flightCharge + dualBaseTotal + dualOwnerFeeTotal + (groundHours * instructorRate) + purchases - referralCredit;
    const fee = total.toFixed(2);
    
    return fee;
  };

  // Recalculate fee live when relevant inputs change
  React.useEffect(() => {
    // Only calculate when lesson tab/editingReservation is available
    try {
      calculateLessonTotal();
    } catch (e) {
      // swallow errors to avoid breaking UI
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonData.simulator, lessonData.flightTime, lessonData.dualReceived, lessonData.ground, lessonData.purchases, lessonData.referralCredit, settings?.simRate, instructionOwnerAircraftFee, aircraftData, editingReservation?.aircraftId, editingReservation?.userIdInstructor, editingReservation?.id, editingReservation?.aircraftRate, editingReservation?.instructorRate, selectedLocationId, locationResources, formData.instructor, formData.aircraft]);

  const fetchLessonData = async (reservationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/lesson/reservation/${reservationId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const lessonRecords = Array.isArray(data) ? data : (data ? [data] : []);
        const currentUserId = Number(currentUser?.user?.id);
        const reservationClientId = Number(editingReservation?.userIdClient);

        let selectedLesson = null;
        if (lessonRecords.length > 0) {
          if (Number.isFinite(currentUserId)) {
            selectedLesson = lessonRecords.find((lesson) => Number(lesson.userId) === currentUserId) || null;
          }

          if (!selectedLesson && Number.isFinite(reservationClientId)) {
            selectedLesson = lessonRecords.find((lesson) => Number(lesson.userId) === reservationClientId) || null;
          }

          if (!selectedLesson) {
            selectedLesson = lessonRecords[0];
          }
        }

        if (!selectedLesson) {
          setLessonFeeOverride(null);
          setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '', referralCredit: '' });
          setLessonId(null);
          return;
        }

        const nextLessonData = {
          simulator: selectedLesson.simulator || '',
          dualReceived: selectedLesson.dualReceived || '',
          ground: selectedLesson.ground || '',
          debrief: selectedLesson.debrief || '',
          nextFlight: selectedLesson.nextFlight || '',
          purchases: selectedLesson.purchases ?? '',
          credit: selectedLesson.credit ?? '',
          flightTime: selectedLesson.flightTime || '',
          payment: selectedLesson.payment ?? '',
          paymentType: selectedLesson.paymentType || '',
          fee: '',
          referralCredit: selectedLesson.referralCredit ?? ''
        };
        setLessonFeeOverride(selectedLesson.fee != null ? String(selectedLesson.fee) : null);
        setLessonData({
          ...nextLessonData,
          fee: selectedLesson.fee ?? computeLessonTotalFrom(nextLessonData)
        });
        setLessonId(selectedLesson.id || null);
      } else {
        setLessonFeeOverride(null);
        setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '', referralCredit: '' });
        setLessonId(null);
      }
    } catch (error) {
      setLessonFeeOverride(null);
      setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', fee: '', referralCredit: '' });
    }
  };

  const saveReservationLessons = async ({ flightTimeOverride = null, hasCompletedAircraftLogOverride = false } = {}) => {
    const selectedAircraftId = `${formData.aircraft || ''}`.trim();
    const lessonAircraftId = selectedAircraftId ? Number(selectedAircraftId) : null;
    const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find((item) => Number(item.id) === lessonAircraftId);
    const shouldApplyInstructionOwnerAircraftFee = selectedAircraftId.length === 0;
    const reservationDate = editingReservation.startDate ? editingReservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    const hasSafetyPilotRental = editingReservation?.type === 'Rental' && !!editingReservation?.safetyPilotId;
    const isConfirmedSafetyPilotRental = hasSafetyPilotRental && getSafetyPilotStatus(editingReservation) === 'confirmed';
    const hasCompletedAircraftLog = hasCompletedAircraftLogOverride || !!(aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd);

    if (isConfirmedSafetyPilotRental && !hasCompletedAircraftLog) {
      throw new Error('Check in the aircraft before saving split renter/safety-pilot lessons');
    }

    const fallbackFlightTime = aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd
      ? parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart || 0)
      : null;
    const parsedFlightTime = flightTimeOverride != null
      ? flightTimeOverride
      : (lessonData.flightTime ? parseFloat(lessonData.flightTime) : fallbackFlightTime);

    let instructorRate = 0;
    const selectedInstructorId = (Array.isArray(instructors) ? instructors : []).find((instructor) => {
      const displayName = `${instructor.firstName || instructor.first_name || ''} ${instructor.lastName || instructor.last_name || ''}`.trim();
      return displayName === formData.instructor || instructor.username === formData.instructor;
    })?.id;
    const lessonInstructorId = selectedInstructorId ?? editingReservation.userIdInstructor;

    if (lessonInstructorId) {
      try {
        const instructorResponse = await fetch(`${API_BASE_URL}/user/${lessonInstructorId}`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        });
        if (instructorResponse.ok) {
          const instructorData = await instructorResponse.json();
          instructorRate = instructorData.rate || 0;
        }
      } catch (error) {
      }
    }

    const simTime = lessonData.simulator ? parseFloat(lessonData.simulator) : 0;
    instructorRate = getBookedRate('instructor', lessonInstructorId, instructors.find(item => Number(item.id) === Number(lessonInstructorId))?.rate ?? instructorRate);
    const flightCharge = reservationAircraft && parsedFlightTime ? (getBookedRate('aircraft', lessonAircraftId, reservationAircraft.rate) * parsedFlightTime) : 0;
    const dual = lessonData.dualReceived ? parseFloat(lessonData.dualReceived) : 0;
    const dualBaseTotal = dual * instructorRate;
    const dualOwnerFeeTotal = shouldApplyInstructionOwnerAircraftFee ? (dual * instructionOwnerAircraftFee) : 0;
    const groundTime = lessonData.ground ? parseFloat(lessonData.ground) : 0;
    const purchases = lessonData.purchases ? parseFloat(lessonData.purchases) : 0;
    const referral = lessonData.referralCredit ? parseFloat(lessonData.referralCredit) : 0;
    const total = flightCharge + dualBaseTotal + dualOwnerFeeTotal + (groundTime * instructorRate) + purchases - referral;

    const toMoney = (value) => Number((value || 0).toFixed(2));
    const totalFee = parseFloat(lessonFeeOverride !== null ? lessonFeeOverride : total.toFixed(2)) || 0;
    const totalCredit = lessonData.credit ? parseFloat(lessonData.credit) : 0;
    const totalPayment = lessonData.payment ? parseFloat(lessonData.payment) : 0;
    const totalPurchases = purchases || 0;
    const totalReferralCredit = referral || 0;
    const renterPctRaw = editingReservation?.renterChargePercentage;
    const renterPct = Number.isFinite(parseFloat(renterPctRaw)) ? Math.max(0, Math.min(100, parseFloat(renterPctRaw))) : 70;
    const safetyPct = 100 - renterPct;
    const shouldSplitRentalLessons = isConfirmedSafetyPilotRental && hasCompletedAircraftLog;

    const displayedLessonPct = shouldSplitRentalLessons
      && currentUser?.user?.orgRole === 'client'
      && Number(currentUser?.user?.id) === Number(editingReservation?.safetyPilotId)
      && Number(currentUser?.user?.id) !== Number(editingReservation?.userIdClient)
        ? safetyPct
        : (shouldSplitRentalLessons ? renterPct : 100);

    setLessonData((previous) => ({
      ...previous,
      flightTime: parsedFlightTime ?? previous.flightTime,
      fee: ((totalFee * displayedLessonPct) / 100).toFixed(2)
    }));

    let existingLessonRecords = [];
    try {
      const existingLessonsResponse = await fetch(`${API_BASE_URL}/lesson/reservation/${editingReservation.id}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (existingLessonsResponse.ok) {
        const existingLessonsData = await existingLessonsResponse.json();
        existingLessonRecords = Array.isArray(existingLessonsData)
          ? existingLessonsData
          : (existingLessonsData ? [existingLessonsData] : []);
      }
    } catch (error) {
      existingLessonRecords = [];
    }

    const renterExistingLessonId = existingLessonRecords.find(
      (lesson) => Number(lesson.userId) === Number(editingReservation.userIdClient)
    )?.id || null;
    const safetyPilotExistingLessonId = existingLessonRecords.find(
      (lesson) => Number(lesson.userId) === Number(editingReservation.safetyPilotId)
    )?.id || null;

    const createLessonPayload = (userId, percentage, existingLessonId = null) => {
      const ratio = percentage / 100;
      const payload = {
        userId,
        reservationId: editingReservation.id,
        date: reservationDate,
        aircraftId: lessonAircraftId,
        instructorId: lessonInstructorId,
        startTime: editingReservation.startTime,
        endTime: editingReservation.endTime,
        flightTime: parsedFlightTime,
        simulator: simTime || null,
        dualReceived: dual || null,
        ground: groundTime || null,
        debrief: lessonData.debrief,
        nextFlight: lessonData.nextFlight,
        purchases: toMoney(totalPurchases * ratio),
        credit: toMoney(totalCredit * ratio),
        payment: toMoney(totalPayment * ratio),
        paymentType: lessonData.paymentType || null,
        referralCredit: toMoney(totalReferralCredit * ratio),
        fee: toMoney(totalFee * ratio)
      };

      if (existingLessonId) {
        payload.id = existingLessonId;
      }

      return payload;
    };

    const renterLessonPayload = createLessonPayload(
      editingReservation.userIdClient,
      shouldSplitRentalLessons ? renterPct : 100,
      renterExistingLessonId || lessonId || null
    );
    const safetyPilotLessonPayload = shouldSplitRentalLessons
      ? createLessonPayload(editingReservation.safetyPilotId, safetyPct, safetyPilotExistingLessonId)
      : null;

    const renterResponse = await fetch(`${API_BASE_URL}/lesson`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'session-id': currentUser?.sessionId
      },
      body: JSON.stringify(renterLessonPayload)
    });

    if (!renterResponse.ok) {
      const errorText = await renterResponse.text();
      throw new Error(errorText || 'Failed to save lesson');
    }

    const renterResult = await renterResponse.json();
    if (renterResult.id) {
      setLessonId(renterResult.id);
    }

    if (safetyPilotLessonPayload) {
      const safetyPilotResponse = await fetch(`${API_BASE_URL}/lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(safetyPilotLessonPayload)
      });

      if (!safetyPilotResponse.ok) {
        const errorText = await safetyPilotResponse.text();
        throw new Error(`Safety pilot lesson failed to save (${errorText})`);
      }
    }

    fetchLessonData(editingReservation.id);

    return {
      splitApplied: !!safetyPilotLessonPayload,
      isRental: editingReservation?.type === 'Rental'
    };
  };

  const fetchReservationFlightLog = async (reservationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/logbook-entry/reservation/${reservationId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setReservationFlightLogEntry(Array.isArray(data) && data.length > 0 ? data[0] : null);
      }
    } catch (error) {
      setReservationFlightLogEntry(null);
    }
  };

  const saveReservationFlightLogEntry = async (entryData) => {
    try {
      const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingReservation?.aircraftId);
      const payload = {
        ...entryData,
        userId: editingReservation?.userIdClient,
        reservationId: editingReservation?.id,
        lessonId: lessonId || null,
        aircraftMake: entryData.aircraftMake || reservationAircraft?.make || reservationAircraft?.manufacturer || '',
        aircraftModel: entryData.aircraftModel || reservationAircraft?.model || '',
        flightDate: entryData.flightDate || (editingReservation?.startDate ? editingReservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0])
      };
      const response = await fetch(`${API_BASE_URL}/logbook-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const saved = await response.json();
        setReservationFlightLogEntry(saved);
        setFlightLogMessage(entryData.id ? 'Logbook entry updated successfully' : 'Logbook entry created successfully');
        setTimeout(() => setFlightLogMessage(''), 3000);
      } else {
        const err = await response.text();
        setFlightLogMessage(`Error: ${err}`);
        setTimeout(() => setFlightLogMessage(''), 5000);
      }
    } catch (error) {
      setFlightLogMessage(`Error: ${error.message}`);
      setTimeout(() => setFlightLogMessage(''), 5000);
    }
  };

  const fetchAircraftLatestLog = async (aircraftId) => {
    try {
      // Try to fetch aircraft maintenance data, fallback to existing aircraft data
      try {
        const aircraftResponse = await fetch(`${API_BASE_URL}/aircraft/${aircraftId}`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        });
        
        if (aircraftResponse.ok) {
          const maintenanceData = await aircraftResponse.json();
          setAircraftMaintenanceData(maintenanceData);
        } else {
          // Fallback to existing aircraft data
          const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === aircraftId);
          if (currentAircraft) {
            setAircraftMaintenanceData(currentAircraft);
          }
        }
      } catch (error) {
        // Fallback to existing aircraft data
        const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === aircraftId);
        if (currentAircraft) {
          setAircraftMaintenanceData(currentAircraft);
        }
      }
      
      const reservationResponse = await fetch(`${API_BASE_URL}/aircraft-log/reservation/${editingReservation.id}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (reservationResponse.ok) {
        const reservationData = await reservationResponse.json();
        setAircraftData(reservationData);
        setLatestAircraftLogId(reservationData.id);
        fetchReservationSquawks({ aircraftLogId: reservationData.id, aircraftId });
        setCheckOutData({
          endHobbs: reservationData.hobbsStart || '',
          endTach: reservationData.tachStart || '',
          remarks: reservationData.remarks || ''
        });
        setCheckInData({
          endHobbs: reservationData.hobbsEnd || '',
          endTach: reservationData.tachEnd || '',
          origin: reservationData.origin || '',
          route: reservationData.route || '',
          destination: reservationData.destination || ''
        });
        setIsCheckedOut(true);
        return;
      }
      
      const latestResponse = await fetch(`${API_BASE_URL}/aircraft/${aircraftId}/log/latest`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (latestResponse.ok) {
        const latestData = await latestResponse.json();
        setAircraftData({ ...latestData, reservationId: null });
        setLatestAircraftLogId(null);
        fetchReservationSquawks({ aircraftId });
        setCheckOutData({
          endHobbs: latestData.hobbsEnd || '',
          endTach: latestData.tachEnd || '',
          remarks: ''
        });
        setIsCheckedOut(false);
      } else {
        setAircraftData(null);
        setLatestAircraftLogId(null);
        setReservationSquawks([]);
        setCheckOutData({ endHobbs: '', endTach: '', remarks: '' });
        setCheckInData({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' });
        setIsCheckedOut(false);
      }
    } catch (error) {
      setAircraftData(null);
      setLatestAircraftLogId(null);
      setReservationSquawks([]);
      setCheckOutData({ endHobbs: '', endTach: '', remarks: '' });
      setCheckInData({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' });
      setIsCheckedOut(false);
    }
  };

  const fetchReservationSquawks = async ({ aircraftLogId = null, aircraftId = null } = {}) => {
    if (!aircraftLogId && !aircraftId) {
      setReservationSquawks([]);
      return;
    }

    setIsLoadingReservationSquawks(true);
    try {
      const endpoint = aircraftLogId
        ? `${API_BASE_URL}/squak/aircraft-log/${aircraftLogId}`
        : `${API_BASE_URL}/squak/aircraft/${aircraftId}`;

      const response = await fetch(endpoint, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        const records = (Array.isArray(data) ? data : []).filter((record) => {
          const status = (record?.status || '').toString().trim().toLowerCase();
          return status !== 'fixed' && status !== 'complete';
        });
        records.sort((a, b) => new Date(b.reportedDatetime || 0) - new Date(a.reportedDatetime || 0));
        setReservationSquawks(records);
      } else {
        setReservationSquawks([]);
      }
    } catch (error) {
      setReservationSquawks([]);
    } finally {
      setIsLoadingReservationSquawks(false);
    }
  };

  const handleAddReservationSquawk = async () => {
    const issue = (newReservationSquawk.issue || '').trim();
    const details = (newReservationSquawk.details || '').trim();

    if (!issue && !details) {
      setReservationSquawkMessage('Error: Please enter issue or details');
      return;
    }

    if (!latestAircraftLogId) {
      setReservationSquawkMessage('Error: Aircraft log not available yet');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/squak`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({
          aircraftId: editingReservation?.aircraftId,
          aircraftLogId: latestAircraftLogId,
          reportedDatetime: new Date().toISOString(),
          status: 'New',
          issue: issue || null,
          details: details || null
        })
      });

      if (response.ok) {
        setNewReservationSquawk({ issue: '', details: '' });
        setReservationSquawkMessage('Squawk saved successfully');
        fetchReservationSquawks({ aircraftLogId: latestAircraftLogId, aircraftId: editingReservation?.aircraftId });
      } else {
        const errorText = await response.text();
        setReservationSquawkMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setReservationSquawkMessage(`Error: ${error.message}`);
    }

    setTimeout(() => setReservationSquawkMessage(''), 3000);
  };

  const openAircraftModal = (plane) => {
    setSelectedAircraftItem(plane);
    setSelectedAircraftImageIndex(0);
    setShowAircraftModal(true);
  };
  const closeAircraftModal = () => {
    setSelectedAircraftItem(null);
    setSelectedAircraftImageIndex(0);
    setShowAircraftModal(false);
    setEnlargedScheduleImage(null);
  };

  const selectedAircraftImages = selectedAircraftItem
    ? (Array.isArray(selectedAircraftItem.images) && selectedAircraftItem.images.length > 0
      ? selectedAircraftItem.images.slice().sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      : selectedAircraftItem.imageUrl ? [{ imageUrl: selectedAircraftItem.imageUrl, sortOrder: 1 }] : [])
    : [];

  const openInstructorModal = (inst) => {
    setSelectedInstructorItem(inst);
    setShowInstructorModal(true);
  };
  const closeInstructorModal = () => {
    setSelectedInstructorItem(null);
    setShowInstructorModal(false);
    setEnlargedScheduleImage(null);
  };

  const handleCheckOut = async () => {
    if (!checkOutData.endHobbs || !checkOutData.endTach) {
      setCheckOutMessage('Error: Please fill in both Start Hobbs and Start Tach');
      return;
    }

    const client = (Array.isArray(people) ? people : []).find(p => p.id === editingReservation.userIdClient);
    const pilotName = client ? (client.firstName || client.first_name || '') : '';

    const reservationDate = editingReservation.startDate ? editingReservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    const payload = {
      aircraftId: editingReservation.aircraftId,
      reservationId: editingReservation.id,
      pilotId: editingReservation.userIdClient,
      pilot: pilotName,
      date: reservationDate,
      hobbsStart: parseFloat(checkOutData.endHobbs),
      hobbsEnd: null,
      tachStart: parseFloat(checkOutData.endTach),
      tachEnd: null,
      remarks: checkOutData.remarks
    };

    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const responseData = await response.json();
        setLatestAircraftLogId(responseData.id);
        fetchReservationSquawks({ aircraftLogId: responseData.id, aircraftId: editingReservation?.aircraftId });
        setCheckOutMessage('Aircraft checked out successfully');
        setIsCheckedOut(true);
        setTimeout(() => setCheckOutMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setCheckOutMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setCheckOutMessage(`Error: ${error.message}`);
    }
  };

  const handleSaveLesson = async () => {
    if (!editingReservation || !canCurrentUserEditReservation(editingReservation)) return;
    if (isSavingLesson) {
      return;
    }

    setIsSavingLesson(true);
    try {
      const result = await saveReservationLessons();
      setLessonMessage(result.splitApplied ? 'Renter and safety pilot lessons saved successfully' : 'Lesson data saved successfully');
      setTimeout(() => setLessonMessage(''), 3000);
    } catch (error) {
      setLessonMessage(`Error: ${error.message}`);
    } finally {
      setIsSavingLesson(false);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInData.endHobbs || !checkInData.endTach) {
      setCheckOutMessage('Error: Please fill in both End Hobbs and End Tach');
      return;
    }

    if (parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs)) {
      setCheckOutMessage('Error: End Hobbs must be higher than Start Hobbs');
      return;
    }

    if (parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach)) {
      setCheckOutMessage('Error: End Tach must be higher than Start Tach');
      return;
    }

    const flightTime = checkInData.endHobbs && checkOutData.endHobbs ? parseFloat(checkInData.endHobbs) - parseFloat(checkOutData.endHobbs) : null;
    const engineTime = checkInData.endTach && checkOutData.endTach ? parseFloat(checkInData.endTach) - parseFloat(checkOutData.endTach) : null;
    
    const client = (Array.isArray(people) ? people : []).find(p => p.id === editingReservation.userIdClient);
    const pilotName = client ? (client.firstName || client.first_name || '') : '';
    const reservationDate = editingReservation.startDate ? editingReservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
    
    const payload = {
      id: latestAircraftLogId,
      aircraftId: editingReservation.aircraftId,
      reservationId: editingReservation.id,
      pilotId: editingReservation.userIdClient,
      pilot: pilotName,
      date: reservationDate,
      hobbsStart: parseFloat(checkOutData.endHobbs),
      hobbsEnd: parseFloat(checkInData.endHobbs),
      tachStart: parseFloat(checkOutData.endTach),
      tachEnd: parseFloat(checkInData.endTach),
      flightTime: flightTime,
      engineTime: engineTime,
      origin: checkInData.origin,
      route: checkInData.route,
      destination: checkInData.destination,
      remarks: checkOutData.remarks
    };

    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const updatedAircraftData = {
          ...aircraftData,
          reservationId: editingReservation.id,
          hobbsEnd: parseFloat(checkInData.endHobbs),
          tachEnd: parseFloat(checkInData.endTach),
          hobbsStart: parseFloat(checkOutData.endHobbs),
          tachStart: parseFloat(checkOutData.endTach)
        };
        setAircraftData(updatedAircraftData);
        fetchReservationSquawks({ aircraftLogId: latestAircraftLogId, aircraftId: editingReservation?.aircraftId });
        if (editingReservation?.type === 'Rental') {
          try {
            const lessonResult = await saveReservationLessons({
              flightTimeOverride: flightTime,
              hasCompletedAircraftLogOverride: true
            });
            setCheckOutMessage(lessonResult.splitApplied
              ? 'Aircraft checked in successfully and renter/safety pilot lessons were created'
              : 'Aircraft checked in successfully and rental lesson was created');
          } catch (lessonError) {
            setCheckOutMessage(`Aircraft checked in successfully, but lesson creation failed: ${lessonError.message}`);
          }
        } else {
          setCheckOutMessage('Aircraft checked in successfully');
        }
        setTimeout(() => setCheckOutMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setCheckOutMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setCheckOutMessage(`Error: ${error.message}`);
    }
  };

  const openEditReservationForm = async (reservation) => {
    if (reservation.locationId) setSelectedLocationId(String(reservation.locationId));
    const instructor = (Array.isArray(people) ? people : []).find(p => p.id === reservation.userIdInstructor);
    const reservationClientId = Number(reservation?.userIdClient ?? reservation?.userId ?? reservation?.user_id);
    const client = (Array.isArray(people) ? people : []).find(p => Number(p.id) === reservationClientId);
    const reservationId = Number(reservation?.id);
    const mappedClient = Number.isFinite(reservationId) && reservationUsersById[reservationId]
      ? { id: reservationClientId, ...reservationUsersById[reservationId] }
      : null;
    const embeddedClient = reservation?.client && Number.isFinite(reservationClientId)
      ? { id: reservationClientId, ...reservation.client }
      : reservation?.client || null;
    const instructorName = instructor ? `${instructor.firstName || instructor.first_name} ${instructor.lastName || instructor.last_name}` : '';
    const clientName = getClientNameForReservation(reservation);
    
    setEditingReservation(reservation);
    setLessonFeeOverride(null);
    setSelectedClientData(client || mappedClient || embeddedClient);
    const reservationForm = {
      date: reservation.startDate ? reservation.startDate.split('T')[0] : '',
      time: convertMilitaryToTime(reservation.startTime),
      endDate: reservation.endDate ? reservation.endDate.split('T')[0] : '',
      endTime: convertMilitaryToTime(reservation.endTime),
      instructor: instructorName.trim(),
      aircraft: reservation.aircraftId || '',
      student: clientName.trim(),
      safetyPilotId: reservation.safetyPilotId || '',
      renterChargePercentage: reservation.renterChargePercentage != null ? `${reservation.renterChargePercentage}` : '70',
      reservationTypeId: reservation.reservationTypeId ?? null,
      type: legacyReservationType(reservation.type),
      comments: reservation.comment || ''
    };
    originalReservationForm.current = reservationForm;
    setFormData(reservationForm);
    setSafetyPilotFilter('');
    setShowSafetyPilotDropdown(false);
    setSelectedManeuvers(reservation.maneuvers ? reservation.maneuvers.map(m => m.id) : []);
    setActiveTab('basic');
    setShowReservationForm(true);

    const createdMeta = getReservationCreatedMeta(reservation);
    const hasCreatedByMeta = Boolean(createdMeta.createdBy);
    const hasCreatedAtMeta = Boolean(createdMeta.createdAtText);

    if (!hasCreatedByMeta || !hasCreatedAtMeta) {
      try {
        const response = await fetch(`${API_BASE_URL}/reservation/${reservation.id}`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        });

        if (response.ok) {
          const reservationData = await response.json();
          const normalizedReservationData = reservationData?.reservation
            || reservationData?.data?.reservation
            || reservationData?.data
            || reservationData?.item
            || reservationData;
          setEditingReservation((prev) => {
            if (!prev || Number(prev.id) !== Number(reservation.id)) {
              return prev;
            }
            return {
              ...prev,
              ...normalizedReservationData,
              meta: reservationData?.meta || prev.meta,
              audit: reservationData?.audit || prev.audit,
              auditTrail: reservationData?.auditTrail || prev.auditTrail
            };
          });
        }
      } catch (error) {
      }
    }
  };

  const openReservationById = async () => {
    if (!canLookupReservationById) {
      return;
    }

    const trimmedId = reservationLookupId.trim();
    const parsedId = Number(trimmedId);
    if (!trimmedId || !Number.isInteger(parsedId) || parsedId <= 0) {
      setReservationLookupMessage('Enter a valid positive reservation ID');
      setReservationLookupMessageType('error');
      setTimeout(() => {
        setReservationLookupMessage('');
        setReservationLookupMessageType('');
      }, 5000);
      return;
    }

    const existingReservation = (Array.isArray(reservations) ? reservations : []).find((reservation) => Number(reservation?.id) === parsedId);
    if (existingReservation) {
      openEditReservationForm(existingReservation);
      setReservationLookupMessage('Opened reservation details');
      setReservationLookupMessageType('success');
      setTimeout(() => {
        setReservationLookupMessage('');
        setReservationLookupMessageType('');
      }, 3000);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reservation/${parsedId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const reservationData = await response.json();
        if (reservationData?.id) {
          openEditReservationForm(reservationData);
          setReservationLookupMessage('Opened reservation details');
          setReservationLookupMessageType('success');
          setTimeout(() => {
            setReservationLookupMessage('');
            setReservationLookupMessageType('');
          }, 3000);
        } else {
          setReservationLookupMessage('Reservation not found');
          setReservationLookupMessageType('error');
          setTimeout(() => {
            setReservationLookupMessage('');
            setReservationLookupMessageType('');
          }, 5000);
        }
      } else {
        const errorText = await response.text();
        setReservationLookupMessage(`Error: ${errorText || 'Unable to find reservation'}`);
        setReservationLookupMessageType('error');
        setTimeout(() => {
          setReservationLookupMessage('');
          setReservationLookupMessageType('');
        }, 5000);
      }
    } catch (error) {
      setReservationLookupMessage(`Error: ${error.message}`);
      setReservationLookupMessageType('error');
      setTimeout(() => {
        setReservationLookupMessage('');
        setReservationLookupMessageType('');
      }, 5000);
    }
  };

  const checkTimeConflict = (newStartDate, newStartTime, newEndDate, newEndTime, excludeId, aircraftId, instructorId) => {
    const toDateTime = (dateValue, timeValue) => {
      const dateString = getReservationDayString(dateValue);
      if (!dateString) return null;
      const militaryTime = toMilitaryNumber(timeValue);
      const hours = Math.floor(militaryTime / 100);
      const minutes = militaryTime % 100;
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day, hours, minutes).getTime();
    };

    const newStart = toDateTime(newStartDate, newStartTime);
    const newEnd = toDateTime(newEndDate, newEndTime);
    if (newStart == null || newEnd == null || newEnd <= newStart) return false;

    return allReservations.some((reservation) => {
      if ((excludeId != null && String(reservation.id) === String(excludeId)) || reservation.status !== 'active') {
        return false;
      }

      const existingStart = toDateTime(
        reservation.startDate ?? reservation.start_date,
        reservation.startTime ?? reservation.start_time
      );
      const existingEnd = toDateTime(
        reservation.endDate ?? reservation.end_date ?? reservation.startDate ?? reservation.start_date,
        reservation.endTime ?? reservation.end_time
      );
      if (existingStart == null || existingEnd == null || newStart >= existingEnd || newEnd <= existingStart) {
        return false;
      }

      const existingAircraftId = reservation.aircraftId ?? reservation.aircraft_id;
      const existingInstructorId = reservation.userIdInstructor ?? reservation.user_id_instructor;
      const aircraftConflict = aircraftId != null && aircraftId !== ''
        && existingAircraftId != null && String(existingAircraftId) === String(aircraftId);
      const instructorConflict = instructorId != null
        && existingInstructorId != null && String(existingInstructorId) === String(instructorId);

      return aircraftConflict || instructorConflict;
    });
  };

  const handleDragStart = (e, reservation, columnType, columnId) => {
    setDraggedReservation(reservation);
    setDraggedColumn({ type: columnType, id: columnId });
    setOriginalPosition({ startTime: reservation.startTime, endTime: reservation.endTime });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnType, columnId, timeSlot) => {
    if (draggedColumn && draggedColumn.type === columnType && draggedColumn.id === columnId && draggedReservation) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const newStartTime = hours * 100 + minutes;
      const duration = draggedReservation.endTime - draggedReservation.startTime;
      const newEndTime = newStartTime + duration;
      
      const highlightedSlots = [];
      let currentTime = newStartTime;
      while (currentTime < newEndTime) {
        const h = Math.floor(currentTime / 100);
        const m = currentTime % 100;
        const slotTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        highlightedSlots.push(slotTime);
        
        // Add 30 minutes properly
        const totalMinutes = h * 60 + m + 30;
        const newHours = Math.floor(totalMinutes / 60);
        const newMinutes = totalMinutes % 60;
        currentTime = newHours * 100 + newMinutes;
      }
      
      setDragOverSlot(null);
    } else {
      e.dataTransfer.dropEffect = 'none';
      setDragOverSlot(null);
    }
  };

  const handleDrop = async (e, timeSlot, columnType, columnId) => {
    e.preventDefault();
    if (!draggedReservation || !draggedColumn || draggedColumn.type !== columnType || draggedColumn.id !== columnId) {
      setDraggedReservation(null);
      setDraggedColumn(null);
      setOriginalPosition(null);
      return;
    }
    if (!canCurrentUserEditReservation(draggedReservation)) return;

    const [hours, minutes] = timeSlot.split(':').map(Number);
    const newStartTime = hours * 100 + minutes;
    
    // Calculate duration in minutes
    const startMinutes = Math.floor(draggedReservation.startTime / 100) * 60 + (draggedReservation.startTime % 100);
    const endMinutes = Math.floor(draggedReservation.endTime / 100) * 60 + (draggedReservation.endTime % 100);
    const durationMinutes = endMinutes - startMinutes;
    
    // Calculate new end time properly
    const newStartMinutes = hours * 60 + minutes;
    const newEndMinutes = newStartMinutes + durationMinutes;
    const newEndHours = Math.floor(newEndMinutes / 60);
    const newEndMins = newEndMinutes % 60;
    const newEndTime = newEndHours * 100 + newEndMins;





    const payload = {
      ...draggedReservation,
      startTime: newStartTime,
      endTime: newEndTime
    };

    const aircraftError = aircraftLocationError(payload.aircraftId,
      new Date(schoolWallClockToIso(String(payload.startDate).slice(0, 10), timeSlot)),
      new Date(schoolWallClockToIso(String(payload.endDate || payload.startDate).slice(0, 10), `${String(newEndHours).padStart(2, '0')}:${String(newEndMins).padStart(2, '0')}`)),
      payload.locationId || selectedLocationId);
    if (aircraftError) {
      window.alert(aircraftError);
      setDraggedReservation(null); setDraggedColumn(null); setOriginalPosition(null); setDragOverSlot(null);
      return;
    }

    if (requiresReservationReason(draggedReservation) && Number(draggedReservation.startTime) !== newStartTime) {
      setPendingSubmitPayload(payload);
      setRescheduleReason('');
      setRescheduleCustomReason('');
      setShowRescheduleModal(true);
      setDraggedReservation(null);
      setDraggedColumn(null);
      setOriginalPosition(null);
      setDragOverSlot(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const { startDate, endDate } = getDateRange();
        fetchReservations(startDate, endDate);
      }
    } catch (error) {
    }

    setDraggedReservation(null);
    setDraggedColumn(null);
    setOriginalPosition(null);
    setDragOverSlot(null);
  };

  const handleCancelReservation = async () => {
    if (isCancelling || !editingReservation) return;
    if (!canCurrentUserEditReservation(editingReservation) || !canCancelReservationAtShortNotice(editingReservation, currentUser?.user)) {
      setMessage('Only an instructor, admin, or superuser can cancel a flight lesson within 24 hours.');
      setIsError(true);
      return;
    }
    const finalReason = (cancelReason === 'other' ? customReason : cancelReason).trim();
    if (requiresReservationReason(editingReservation) && !finalReason) {
      setMessage('A reason is required to cancel a reservation within 28 hours.');
      setIsError(true);
      return;
    }
    setIsCancelling(true);
    
    const payload = {
      id: editingReservation.id,
      status: 'cancelled',
      ...(finalReason ? { cancellationReason: finalReason } : {})
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setMessage('Reservation Cancelled Successfully');
        setIsError(false);
        setShowCancelModal(false);
        setTimeout(() => {
          const { startDate, endDate } = getDateRange();
          fetchReservations(startDate, endDate);
          setShowReservationForm(false);
          setEditingReservation(null);
          setClientFilter('');
          setMessage('');
          setCancelReason('');
          setCustomReason('');
        }, 3000);
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to cancel reservation';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || 'Failed to cancel reservation';
        } catch {
          errorMessage = errorText || 'Failed to cancel reservation';
        }
        setMessage(`Error: ${errorMessage}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage('Error: Error cancelling reservation');
      setIsError(true);
    } finally {
      setIsCancelling(false);
    }
  };



  const renderDayView = () => {
    const timeSlots = [];
    const selectedDateString = getDateString(date);
    const startHour = settings?.flightReservationDayStart || 6;
    const endHour = settings?.flightReservationDayEnd || 22;
    for (let hour = startHour; hour < endHour; hour++) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
      timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }

    const calculateReservationHeight = (startTime, endTime) => {
      const startMinutes = Math.floor(startTime / 100) * 60 + (startTime % 100);
      const endMinutes = Math.floor(endTime / 100) * 60 + (endTime % 100);
      const durationMinutes = endMinutes - startMinutes;
      const slots = durationMinutes / 30;
      return Math.max(slots * 31, 31);
    };

    const activeInstructors = (Array.isArray(instructors) ? instructors : [])
      .filter(instructor => String(instructor.status || '').trim().toLowerCase() === 'active')
      .slice()
      .sort((a, b) => {
        const orderDiff = toSortOrderValue(a.sortOrder) - toSortOrderValue(b.sortOrder);
        if (orderDiff !== 0) return orderDiff;
        const aName = `${a.firstName || a.first_name || a.username || ''}`.toLowerCase();
        const bName = `${b.firstName || b.first_name || b.username || ''}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    const isAircraftAvailableAtTime = (plane, timeSlot, ownScheduleOnly = false) => {
      const schedule = plane.availabilitySchedule;
      const slotInstant = schoolWallClockToIso(selectedDateString, timeSlot);
      return !!slotInstant && isLocationScheduleAvailable(schedule, new Date(slotInstant), !selectedLocationId || !!selectedLocation?.isDefault) &&
        (ownScheduleOnly || !aircraftLocationError(plane.id, new Date(slotInstant), new Date(Date.parse(slotInstant) + 30 * 60000)));
    };
    const filteredAircraft = (Array.isArray(aircraft) ? aircraft : [])
      .filter(plane => timeSlots.some(timeSlot => isAircraftAvailableAtTime(plane, timeSlot, true)))
      .slice()
      .sort((a, b) => {
        const orderDiff = toSortOrderValue(a.sortOrder) - toSortOrderValue(b.sortOrder);
        if (orderDiff !== 0) return orderDiff;
        const aName = `${a.make || a.manufacturer || ''} ${a.model || ''} ${a.tailNumber || a.registration || ''}`.toLowerCase();
        const bName = `${b.make || b.manufacturer || ''} ${b.model || ''} ${b.tailNumber || b.registration || ''}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    const hasAircraft = filteredAircraft && filteredAircraft.length > 0;
    const hasInstructors = activeInstructors.length > 0;
    
    let gridColumns = '80px';
    if (hasAircraft) {
      gridColumns += ` repeat(${filteredAircraft.length}, 1fr)`;
      if (hasInstructors) {
        gridColumns += ' 3px';
      }
    }
    if (hasInstructors) {
      gridColumns += ` repeat(${activeInstructors.length}, 1fr)`;
    }
    
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <div className="flight-schedule-day-grid" style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: '1px', backgroundColor: '#ccc', minWidth: '800px' }}>
          <div style={{ backgroundColor: '#f0f0f0', padding: '0.25rem', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 10, fontSize: '0.8rem' }}>Time</div>
          
          {hasAircraft && filteredAircraft.map(plane => {
            const dateStr = getDateString(date);
            const hasBlockout = getAircraftBlockoutForDate(plane.id, dateStr).length > 0;
            const isStatusBlocked = plane.status === 'Maintenance' || plane.status === 'Unavailable';
            const headerBlocked = isStatusBlocked || hasBlockout;
            const blockoutReasons = getAircraftBlockoutForDate(plane.id, dateStr).map(b => b.reason).filter(Boolean).join(', ');
            const headerTitle = isStatusBlocked ? plane.status : (hasBlockout ? `Blockout${blockoutReasons ? ': ' + blockoutReasons : ''}` : undefined);
            return (
            <div key={`aircraft-${plane.id}`} style={{ backgroundColor: headerBlocked ? '#d3d3d3' : '#e8f4fd', padding: '0.25rem', fontWeight: 'bold', textAlign: 'center', fontSize: '0.7rem', position: 'sticky', top: 0, zIndex: 10, color: headerBlocked ? '#888' : 'inherit' }} title={headerTitle}>
                <button onClick={(e) => { e.stopPropagation(); openAircraftModal(plane); }} style={{ background: '#e9f7ff', border: '1px solid #007bff', padding: '6px 6px', margin: 0, cursor: 'pointer', width: '100%', textAlign: 'center', borderRadius: '8px', boxShadow: '0 6px 12px rgba(0,123,255,0.12)', height: '36px', minHeight: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }} title="Open aircraft details">
                  <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#007bff', lineHeight: '1' }}>{plane.make || plane.manufacturer || ''} {plane.model || ''}</div>
                  <div style={{ fontSize: '0.75rem', color: headerBlocked ? '#888' : '#333', lineHeight: '1' }}>{plane.tailNumber || plane.registration || ''}</div>
                </button>
            </div>
            );
          })}
          
          {hasAircraft && hasInstructors && <div style={{ backgroundColor: '#333', position: 'sticky', top: 0, zIndex: 10 }}></div>}
          
          {hasInstructors && activeInstructors.map(instructor => (
            <div key={`instructor-${instructor.id}`} style={{ backgroundColor: '#f0f8e8', padding: '0.25rem', fontWeight: 'bold', textAlign: 'center', fontSize: '0.7rem', position: 'sticky', top: 0, zIndex: 10 }}>
              <button onClick={(e) => { e.stopPropagation(); openInstructorModal(instructor); }} style={{ background: '#e9fef0', border: '1px solid #28a745', padding: '6px 6px', margin: 0, cursor: 'pointer', width: '100%', textAlign: 'center', borderRadius: '8px', boxShadow: '0 6px 12px rgba(33,136,56,0.12)', height: '36px', minHeight: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }} title="Open instructor details">
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#218838', lineHeight: '1' }}>{(instructor.firstName || instructor.first_name || instructor.username || '')}</div>
              </button>
            </div>
          ))}
          
          {timeSlots.map((timeSlot, timeIndex) => (
            <React.Fragment key={timeSlot}>
              <div style={{ backgroundColor: '#f0f0f0', padding: '0.25rem', textAlign: 'center', fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 1, fontSize: '0.8rem' }}>
                {(() => {
                  const [hour, minute] = timeSlot.split(':');
                  const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                  const ampm = hour < 12 ? 'AM' : 'PM';
                  return `${h12}:${minute} ${ampm}`;
                })()}
              </div>
              
              {hasAircraft && filteredAircraft.map((plane, planeIndex) => {
                const dateStr = getDateString(date);
                const dayReservations = reservations.filter(r => {
                  const reservationDate = r.startDate ? r.startDate.split('T')[0] : '';
                  const matchesDate = reservationDate === dateStr;
                  // eslint-disable-next-line eqeqeq
                  const matchesAircraft = r.aircraftId == plane.id;
                  return matchesDate && matchesAircraft && r.status === 'active';
                });
                
                const currentReservation = dayReservations.find(r => {
                  const startTime = convertMilitaryToTime(r.startTime);
                  return startTime === timeSlot;
                });
                
                const isStatusBlocked = plane.status === 'Maintenance' || plane.status === 'Unavailable';
                const isBlockedOut = isAircraftBlockedOut(plane.id, dateStr, timeSlot);
                const isScheduleUnavailable = !isAircraftAvailableAtTime(plane, timeSlot);
                const slotStart = new Date(schoolWallClockToIso(dateStr, timeSlot));
                const aircraftTravelError = aircraftLocationError(plane.id, slotStart, new Date(slotStart.getTime() + 30 * 60000));
                const isCellBlocked = isStatusBlocked || isBlockedOut || isScheduleUnavailable;
                const blockoutForSlot = isBlockedOut ? aircraftBlockouts.find(b => {
                  // eslint-disable-next-line eqeqeq
                  if (b.aircraftId != plane.id) return false;
                  const [h, m] = timeSlot.split(':').map(Number);
                  const slotDate = new Date(`${dateStr}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
                  return slotDate >= new Date(b.startDatetime) && slotDate < new Date(b.endDatetime);
                }) : null;

                return (
                  <div key={`${timeSlot}-aircraft-${plane.id}`} 
                       style={{ backgroundColor: isCellBlocked ? '#e8e8e8' : 'white', padding: '0.25rem', minHeight: '30px', cursor: isCellBlocked ? 'not-allowed' : 'pointer', position: 'relative', zIndex: currentReservation ? 2 : undefined, opacity: isCellBlocked ? 0.6 : 1 }}
                       title={isStatusBlocked ? plane.status : (isBlockedOut ? `Blockout${blockoutForSlot?.reason ? ': ' + blockoutForSlot.reason : ''}` : aircraftTravelError || (isScheduleUnavailable ? 'Outside aircraft availability hours' : undefined))}
                       onClick={!isCellBlocked ? () => openReservationForm(date, '', plane.id, timeSlot) : undefined}
                       onDragOver={!isCellBlocked ? (e) => handleDragOver(e, 'aircraft', plane.id, timeSlot) : undefined}
                       onDrop={!isCellBlocked ? (e) => handleDrop(e, timeSlot, 'aircraft', plane.id) : undefined}>
                    {currentReservation && (
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        height: `${calculateReservationHeight(currentReservation.startTime, currentReservation.endTime)}px`,
                        backgroundColor: getReservationColor(currentReservation.type), 
                        border: '2px solid #007bff', 
                        padding: '4px', 
                        fontSize: '12px',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: !canClientAccessReservation(currentReservation)
                          ? 'not-allowed'
                          : (canCurrentUserEditReservation(currentReservation) ? 'move' : 'pointer'),
                        textAlign: 'left'
                      }}
                      draggable={canCurrentUserEditReservation(currentReservation)}
                      onDragStart={canCurrentUserEditReservation(currentReservation) ? (e) => handleDragStart(e, currentReservation, 'aircraft', plane.id) : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canClientAccessReservation(currentReservation)) {
                          return;
                        }
                        openEditReservationForm(currentReservation);
                      }}>
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '6px',
                          padding: '2px 4px',
                          cursor: !canClientAccessReservation(currentReservation) ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(0, 123, 255, 0.3)',
                          margin: '2px',
                          width: 'calc(100% - 4px)'
                        }}>
                          {getReservationDisplay(currentReservation, 'day')}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {hasAircraft && hasInstructors && <div style={{ backgroundColor: '#333' }}></div>}
              
              {hasInstructors && activeInstructors.map(instructor => {
                const instructorName = `${instructor.firstName || instructor.first_name || ''} ${instructor.lastName || instructor.last_name || ''}`.trim();
                const dayReservations = reservations.filter(r => {
                  const dateStr = getDateString(date);
                  const reservationDate = r.startDate ? r.startDate.split('T')[0] : '';
                  const matchesDate = reservationDate === dateStr;
                  const matchesInstructor = r.userIdInstructor === instructor.id;
                  return matchesDate && matchesInstructor && r.status === 'active';
                });
                
                const currentReservation = dayReservations.find(r => {
                  const startTime = convertMilitaryToTime(r.startTime);
                  return startTime === timeSlot;
                });
                
                const isAvailable = isTimeSlotAvailable(instructor, timeSlot, date);
                const overrideStatus = getOverrideStatus(instructor, timeSlot, date);
                const isGrayedOut = overrideStatus === 'off' || (!isAvailable && overrideStatus !== 'on');
                const finalAvailable = (isAvailable || overrideStatus === 'on') && overrideStatus !== 'off';
                const canSelfScheduleInOwnColumn = isCurrentUserInstructor(instructor.id) && !currentReservation;
                const canOpenReservation = finalAvailable || canSelfScheduleInOwnColumn;
                
                return (
                  <div key={`${timeSlot}-instructor-${instructor.id}`} 
                       style={{ 
                         backgroundColor: isGrayedOut ? '#d3d3d3' : (isAvailable ? 'white' : '#f0f0f0'), 
                         padding: '0.25rem', 
                         minHeight: '30px', 
                         cursor: canOpenReservation ? 'pointer' : 'not-allowed', 
                         position: 'relative',
                         zIndex: currentReservation ? 2 : undefined,
                         opacity: finalAvailable ? 1 : 0.6
                       }}
                       title={canSelfScheduleInOwnColumn && !finalAvailable ? 'Self-scheduling is allowed' : undefined}
                       onClick={canOpenReservation ? () => openReservationForm(date, instructorName, '', timeSlot) : undefined}
                       onDragOver={finalAvailable ? (e) => handleDragOver(e, 'instructor', instructor.id, timeSlot) : undefined}
                       onDrop={finalAvailable ? (e) => handleDrop(e, timeSlot, 'instructor', instructor.id) : undefined}>
                    {currentReservation && (
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        height: `${calculateReservationHeight(currentReservation.startTime, currentReservation.endTime)}px`,
                        backgroundColor: getReservationColor(currentReservation.type), 
                        border: '2px solid #007bff', 
                        padding: '4px', 
                        fontSize: '12px',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: !canClientAccessReservation(currentReservation)
                          ? 'not-allowed'
                          : (canCurrentUserEditReservation(currentReservation) ? 'move' : 'pointer'),
                        textAlign: 'left'
                      }}
                      draggable={canCurrentUserEditReservation(currentReservation)}
                      onDragStart={canCurrentUserEditReservation(currentReservation) ? (e) => handleDragStart(e, currentReservation, 'instructor', instructor.id) : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canClientAccessReservation(currentReservation)) {
                          return;
                        }
                        openEditReservationForm(currentReservation);
                      }}>
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '6px',
                          padding: '2px 4px',
                          cursor: !canClientAccessReservation(currentReservation) ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(0, 123, 255, 0.3)',
                          margin: '2px',
                          width: 'calc(100% - 4px)'
                        }}>
                          {getReservationDisplay(currentReservation, 'day')}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(date);
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(180px, 1fr))', gap: '1px', backgroundColor: '#ccc', minWidth: '1200px', height: 'calc(100vh - 200px)', overflow: 'auto' }}>
        {weekDates.map((day) => {
          const dayReservations = sortReservationsByStartTime(getReservationsForDate(day));
          return (
            <div key={day.toISOString()} style={{ backgroundColor: 'white', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
              <div style={{ backgroundColor: '#e8f4fd', padding: '0.5rem', fontWeight: 'bold', textAlign: 'center', fontSize: '0.85rem', borderBottom: '1px solid #d0d0d0', position: 'sticky', top: 0, zIndex: 2 }}>
                {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
              <div style={{ padding: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', minHeight: '120px' }}>
                {dayReservations.length === 0 && (
                  <div
                    style={{ color: '#8a8a8a', fontSize: '0.75rem', border: '1px dashed #d4d4d4', borderRadius: '6px', padding: '0.35rem', cursor: 'pointer' }}
                    onClick={() => openReservationForm(day)}
                  >
                    No reservations
                  </div>
                )}
                {dayReservations.map((reservation) => (
                  <div
                    key={getReservationSortKey(reservation)}
                    style={{
                      fontSize: '0.7rem',
                      backgroundColor: getReservationColor(reservation.type),
                      padding: '4px 6px',
                      borderRadius: '6px',
                      border: '1px solid rgba(0, 123, 255, 0.25)',
                      cursor: !canClientAccessReservation(reservation) ? 'not-allowed' : 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canClientAccessReservation(reservation)) {
                        return;
                      }
                      openEditReservationForm(reservation);
                    }}
                  >
                    {getReservationDisplay(reservation, 'week')}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(date);
    const currentMonth = date.getMonth();
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: '#ccc', height: 'calc(100vh - 200px)', minWidth: '800px' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
            {day}
          </div>
        ))}
        
        {monthDates.map(day => {
          const dayReservations = getReservationsForDate(day);
          const isCurrentMonth = day.getMonth() === currentMonth;
          
          return (
            <div key={day.toISOString()} 
                 style={{ 
                   backgroundColor: 'white', 
                   height: '100px',
                   cursor: 'pointer',
                   opacity: isCurrentMonth ? 1 : 0.3,
                   display: 'flex',
                   flexDirection: 'column'
                 }}
                 onClick={() => openReservationForm(day)}>
              <div style={{ fontWeight: 'bold', padding: '0.25rem', flexShrink: 0 }}>{day.getDate()}</div>
              <div style={{ flex: 1, overflow: 'auto', padding: '0.25rem' }}>
                {dayReservations.map(res => (
                  <div key={res.id} style={{ fontSize: '0.7rem', backgroundColor: '#e3f2fd', padding: '1px', margin: '1px', borderRadius: '2px', cursor: !canClientAccessReservation(res) ? 'not-allowed' : 'pointer' }}
                       onClick={(e) => {
                         e.stopPropagation();
                         if (!canClientAccessReservation(res)) {
                           return;
                         }
                         openEditReservationForm(res);
                       }}>
                    {getReservationDisplay(res, 'month')}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <WelcomeMessage hoursRefreshKey={scheduleHoursRefreshKey} />
      {assistantSchedulerError && <p role="alert" style={{ margin: 0, padding: '0.5rem 1rem', color: '#b91c1c' }}>{assistantSchedulerError}</p>}
      {instructorError && <div role="alert" style={{ padding: '0.5rem 1rem', color: '#b91c1c' }}>Instructor details could not be loaded: {instructorError}</div>}
      {locationAircraftSchedulesError && <div role="alert" style={{ padding: '0.5rem 1rem', color: '#b91c1c' }}>{locationAircraftSchedulesError} <button type="button" onClick={() => fetchLocationAircraftSchedules?.()}>Reload aircraft schedules</button></div>}
      {loadingLocationAircraftSchedules && <div role="status">Loading aircraft location schedules...</div>}
      {locationError && locations.length <= 1 && <div role="alert" style={{ padding: '0.5rem 1rem', color: '#b91c1c' }}>{locationError}</div>}
      {googleCalendarBanner && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: googleCalendarBanner.type === 'error' ? '#f8d7da' : '#d4edda', color: googleCalendarBanner.type === 'error' ? '#721c24' : '#155724', borderBottom: `1px solid ${googleCalendarBanner.type === 'error' ? '#f5c6cb' : '#c3e6cb'}` }}>
          <span>{googleCalendarBanner.text}</span>
          <button onClick={() => setGoogleCalendarBanner(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'inherit' }}>×</button>
        </div>
      )}

      <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>
            {view === 'day' ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : `Calendar - ${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setView('day')} style={{ backgroundColor: view === 'day' ? '#007bff' : '#fff', color: view === 'day' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Day</button>
            <button onClick={() => setView('week')} style={{ backgroundColor: view === 'week' ? '#007bff' : '#fff', color: view === 'week' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Week</button>
            <button onClick={() => setView('month')} style={{ backgroundColor: view === 'month' ? '#007bff' : '#fff', color: view === 'month' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Month</button>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => {
              const newDate = new Date(date);
              if (view === 'day') newDate.setDate(date.getDate() - 1);
              else if (view === 'week') newDate.setDate(date.getDate() - 7);
              else newDate.setMonth(date.getMonth() - 1);
              setDate(newDate);
            }}>← Previous</button>
            
            <button onClick={() => setDate(new Date())}>Today</button>
            
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button style={{ padding: '0.5rem', backgroundColor: 'transparent', color: '#333', border: 'none', cursor: 'pointer', fontSize: '1.2rem', pointerEvents: 'none' }}>
                📅
              </button>
              <input 
                ref={dateInputRef}
                type="date" 
                onChange={(e) => {
                  const [year, month, day] = e.target.value.split('-').map(Number);
                  const selectedDate = new Date(year, month - 1, day);
                  setDate(selectedDate);
                }}
                style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', width: '1.2rem', height: '1.2rem', opacity: 0, cursor: 'pointer' }}
              />
            </div>
            
            <button onClick={() => {
              const newDate = new Date(date);
              if (view === 'day') newDate.setDate(date.getDate() + 1);
              else if (view === 'week') newDate.setDate(date.getDate() + 7);
              else newDate.setMonth(date.getMonth() + 1);
              setDate(newDate);
            }}>Next →</button>
            {locations.length > 1 && <label htmlFor="schedule-location" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: '2in' }}>
              Location
              <select id="schedule-location" value={selectedLocationId} disabled={showReservationForm} onChange={event => setSelectedLocationId(event.target.value)} style={{ padding: '0.45rem', minWidth: '220px' }}>
                {locations.map(place => <option key={place.id} value={place.id}>{getLocationDisplayName(place)}{place.airportCode ? ` (${place.airportCode})` : place.airport ? ` (${place.airport})` : ''}</option>)}
              </select>
            </label>}
            {locationError && locations.length > 1 && <span role="alert" style={{ color: '#b91c1c' }}>{locationError}</span>}
          </div>

          {canLookupReservationById && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: '600' }}>Open Reservation By ID:</label>
                <input
                  type="text"
                  value={reservationLookupId}
                  onChange={(e) => setReservationLookupId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      openReservationById();
                    }
                  }}
                  placeholder="Enter reservation ID"
                  style={{ padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '190px' }}
                />
                <button
                  onClick={openReservationById}
                  style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.45rem 0.8rem', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Open
                </button>
              </div>
              {reservationLookupMessage && (
                <div style={{
                  marginTop: '0.6rem',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  backgroundColor: reservationLookupMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: reservationLookupMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${reservationLookupMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {reservationLookupMessage}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="flight-schedule-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative' }}>
        <LoadingSpinner isLoading={isLoadingReservations} />
        {view === 'day' && renderDayView()}
        {view === 'week' && renderWeekView()}
        {view === 'month' && renderMonthView()}
      </div>

      {showReservationForm && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '520px', height: '660px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ position: 'relative', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>
              {editingReservation ? (
                formData.student && canViewClientNames ? (
                  <>
                    {isCurrentUserAssignedSafetyPilot(editingReservation) ? 'View Reservation - ' : 'Edit Reservation - '}
                    <span 
                      onClick={() => {
                        if (!canViewClientNames || (editingReservation && isCurrentUserAssignedSafetyPilot(editingReservation))) {
                          return;
                        }
                        setShowClientDialog(true);
                      }}
                      style={!canViewClientNames || (editingReservation && isCurrentUserAssignedSafetyPilot(editingReservation))
                        ? { cursor: 'default', color: '#333' }
                        : { cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                    >
                      {canViewClientNames ? formData.student : 'Private'}
                    </span>
                  </>
                ) : 'Edit Reservation'
              ) : (
                formData.student && canViewClientNames ? (
                  <>
                    New Reservation - {formData.student}
                  </>
                ) : 'New Reservation'
              )}
            </h3>
            <button onClick={() => { setShowReservationForm(false); setEditingReservation(null); setSelectedManeuvers([]); setActiveTab('basic'); setClientFilter(''); setSafetyPilotFilter(''); setShowSafetyPilotDropdown(false); setMessage(''); setIsSubmitting(false); setShowManeuverSelectionConfirmModal(false); setAircraftData(null); setAircraftMaintenanceData(null); setCheckOutData({ endHobbs: '', endTach: '', remarks: '' }); setCheckInData({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' }); setCheckOutMessage(''); setIsCheckedOut(false); setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '' }); setLessonMessage(''); setLessonId(null); setReservationFlightLogEntry(null); setFlightLogMessage(''); setReservationSquawks([]); setReservationSquawkMessage(''); setNewReservationSquawk({ issue: '', details: '' }); }} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
          </div>
          
          {showReservationManeuvers || formData.type === 'Checkride' || (editingReservation && editingReservation.aircraftId) || (editingReservation && (editingReservation.type === 'Flight Training' || editingReservation.type === 'Ground lesson' || editingReservation.type === 'Discovery' || editingReservation.type === 'Solo' || editingReservation.type === 'Rental' || editingReservation.type === 'Checkride')) ? (
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setActiveTab('basic')} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: activeTab === 'basic' ? '#e6f3ff' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: activeTab === 'basic' ? '1px solid #e6f3ff' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeTab === 'basic' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Basic Info</button>
              {showReservationManeuvers && (
                <button type="button" onClick={() => setActiveTab('maneuvers')} style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: activeTab === 'maneuvers' ? '#f0f8f0' : '#e9ecef', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: activeTab === 'maneuvers' ? '1px solid #f0f8f0' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'maneuvers' ? 'bold' : 'normal',
                  marginRight: '2px'
                }}>Maneuvers</button>
              )}
              {editingReservation && editingReservation.aircraftId && (
                <button type="button" onClick={() => {
                  setActiveTab('aircraft');
                  if (!aircraftMaintenanceData) {
                    fetchAircraftLatestLog(editingReservation.aircraftId);
                  }
                  if (latestAircraftLogId) {
                    fetchReservationSquawks({ aircraftLogId: latestAircraftLogId, aircraftId: editingReservation.aircraftId });
                  } else {
                    fetchReservationSquawks({ aircraftId: editingReservation.aircraftId });
                  }
                }} style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: activeTab === 'aircraft' ? '#fff2e6' : '#e9ecef', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: activeTab === 'aircraft' ? '1px solid #fff2e6' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'aircraft' ? 'bold' : 'normal',
                  marginRight: '2px'
                }}>Aircraft</button>
              )}
              {(editingReservation && ((editingReservation.type === 'Flight Training' || editingReservation.type === 'Ground lesson' || editingReservation.type === 'Discovery' || editingReservation.type === 'Solo' || editingReservation.type === 'Rental' || editingReservation.type === 'Checkride') || !editingReservation.userIdInstructor)) || formData.type === 'Checkride' ? (
                <button type="button" onClick={() => {
                  setActiveTab('lesson');
                  if (editingReservation) fetchLessonData(editingReservation.id);
                  if (editingReservation && editingReservation.aircraftId && !aircraftData) {
                    fetchAircraftLatestLog(editingReservation.aircraftId);
                  }
                }} style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: activeTab === 'lesson' ? '#f0f0ff' : '#e9ecef', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: activeTab === 'lesson' ? '1px solid #f0f0ff' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'lesson' ? 'bold' : 'normal'
                }}>{editingReservation?.type === 'Checkride' ? 'Flight Log' : editingReservation ? (editingReservation.userIdInstructor ? 'Lesson' : 'Flight Log') : 'Lesson'}</button>
              ) : null}
              </div>
            ) : null}

            <form id="reservation-form" onSubmit={handleSubmit} style={{ flex: 1, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowY: 'auto', overflowX: 'hidden', minWidth: 0, padding: '1rem' }}>
              <fieldset style={{ border: 'none', margin: 0, padding: 0, minWidth: 0, width: '100%', maxWidth: '100%' }}>

              {activeTab === 'basic' && (
                <>
                {locations.length > 1 && <div style={{ marginBottom: '1rem', color: '#334155' }}><strong>Location:</strong> {getLocationDisplayName(selectedLocation)}{selectedLocation?.airportCode ? ` (${selectedLocation.airportCode})` : selectedLocation?.airport ? ` (${selectedLocation.airport})` : ''}</div>}
                {checkingAvailability && <p role="status">{locations.length > 1 ? 'Checking location availability...' : 'Checking availability...'}</p>}
                {availabilityError && <p role="alert" style={{ color: '#b91c1c' }}>{availabilityError}</p>}
                {editingReservation?.id && (
                  <div
                    style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    title={getReservationCreatedMeta(editingReservation).title || `Reservation ID: ${editingReservation.id}`}
                  >
                    <label>Reservation ID:</label>
                    <span style={{ minWidth: '200px', textAlign: 'right', color: '#495057', fontWeight: 600 }}>
                      {editingReservation.id}
                    </span>
                  </div>
                )}

                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label
                    title={
                      (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && editingReservation?.rescheduleReason
                        ? editingReservation.rescheduleReason
                        : undefined
                    }
                  >
                    Start Date:
                  </label>
                  <input type="date" value={formData.date} onChange={(e) => {
                    const newStartDate = e.target.value;
                    const oldStartDate = new Date(formData.date);
                    const oldEndDate = new Date(formData.endDate);
                    const daysDiff = Math.round((oldEndDate - oldStartDate) / (1000 * 60 * 60 * 24));
                    
                    const newEndDate = new Date(newStartDate);
                    newEndDate.setDate(newEndDate.getDate() + daysDiff);
                    const newEndDateStr = newEndDate.toISOString().split('T')[0];
                    
                    setFormData({...formData, date: newStartDate, endDate: newEndDateStr});
                  }} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Start Time:</label>
                  <select value={formData.time} onChange={(e) => {
                    const newTime = e.target.value;
                    const updatedFormData = applyReservationDuration({ ...formData, time: newTime }, selectedReservationType);
                    setFormData(updatedFormData);
                  }} required>
                    <option value="">Select Time</option>
                    {Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>End Date:</label>
                  <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>End Time:</label>
                  <select value={formData.endTime} onChange={(e) => setFormData({...formData, endTime: e.target.value})} required>
                    <option value="">Select Time</option>
                    {[...new Set([...Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat(), ...(formData.endTime ? [formData.endTime] : [])])].sort().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Instructor:</label>
                  <select value={formData.instructor} onChange={(e) => setFormData({...formData, instructor: e.target.value})}>
                    <option value="">Select Instructor</option>
                    {(Array.isArray(instructors) ? instructors : [])
                      .filter(instructor => {
                      if (String(instructor.status || '').trim().toLowerCase() !== 'active') return false;
                      const isSelfScheduling = isCurrentUserInstructor(instructor.id);
                      if (bookingAvailability && !(bookingAvailability.instructors || []).some(item => Number(item.id) === Number(instructor.id)) && Number(editingReservation?.userIdInstructor) !== Number(instructor.id) && !isSelfScheduling) return false;
                      
                      // If aircraft is selected, check if instructor has this aircraft in their aircraftList
                      if (formData.aircraft) {
                        const aircraftList = instructor.aircraftList || [];
                        return aircraftList.some(ac => 
                          ac.aircraftId == formData.aircraft && 
                          ac.limitations && ac.limitations.toLowerCase() === 'instruct'
                        );
                      }
                      
                      return true;
                    })
                      .slice()
                      .sort((a, b) => {
                        const orderDiff = toSortOrderValue(a.sortOrder) - toSortOrderValue(b.sortOrder);
                        if (orderDiff !== 0) return orderDiff;
                        const aName = `${a.firstName || a.first_name || a.username || ''}`.toLowerCase();
                        const bName = `${b.firstName || b.first_name || b.username || ''}`.toLowerCase();
                        return aName.localeCompare(bName);
                      })
                      .map(instructor => {
                      const displayName = instructor.firstName && instructor.lastName ? `${instructor.firstName} ${instructor.lastName}` : instructor.username;
                      return (
                        <option key={instructor.id} value={displayName}>{displayName}</option>
                      );
                    })}
                  </select>
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Aircraft:</label>
                  <select value={formData.aircraft} onChange={(e) => setFormData({...formData, aircraft: e.target.value})}>
                    <option value="">Select Aircraft</option>
                    {(Array.isArray(aircraft) ? aircraft : [])
                      .filter(plane => {
                      if (bookingAvailability && !(bookingAvailability.aircraft || []).some(item => Number(item.id) === Number(plane.id)) && Number(editingReservation?.aircraftId) !== Number(plane.id)) return false;
                      if (formData.date && formData.time && formData.endTime && aircraftLocationError(plane.id,
                        new Date(schoolWallClockToIso(formData.date, formData.time)),
                        new Date(schoolWallClockToIso(formData.endDate || formData.date, formData.endTime)),
                        editingReservation?.locationId || selectedLocationId)) return false;
                      if (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') {
                        return true;
                      }
                      return plane.status !== 'Unavailable';
                    })
                      .slice()
                      .sort((a, b) => {
                        const orderDiff = toSortOrderValue(a.sortOrder) - toSortOrderValue(b.sortOrder);
                        if (orderDiff !== 0) return orderDiff;
                        const aName = `${a.make || a.manufacturer || ''} ${a.model || ''} ${a.tailNumber || a.registration || ''}`.toLowerCase();
                        const bName = `${b.make || b.manufacturer || ''} ${b.model || ''} ${b.tailNumber || b.registration || ''}`.toLowerCase();
                        return aName.localeCompare(bName);
                      })
                      .map(plane => (
                      <option key={plane.id} value={plane.id}>{plane.model} - {plane.tailNumber || plane.registration}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Client:</label>
                  {currentUser?.user?.orgRole === 'client' ? (
                    <select value={formData.student} onChange={(e) => setFormData({...formData, student: e.target.value})} style={{ width: '200px', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                      <option value={(() => {
                        const currentUserName = `${currentUser?.user?.firstName || currentUser?.firstName || ''} ${currentUser?.user?.lastName || currentUser?.lastName || ''}`.trim();
                        const isSafetyPilotViewer = editingReservation && Number(currentUser?.user?.id) === Number(editingReservation?.safetyPilotId);
                        return isSafetyPilotViewer && formData.student ? formData.student : currentUserName;
                      })()}>{(() => {
                        const currentUserName = `${currentUser?.user?.firstName || currentUser?.firstName || ''} ${currentUser?.user?.lastName || currentUser?.lastName || ''}`.trim();
                        const isSafetyPilotViewer = editingReservation && Number(currentUser?.user?.id) === Number(editingReservation?.safetyPilotId);
                        return isSafetyPilotViewer && formData.student ? formData.student : currentUserName;
                      })()}</option>
                    </select>
                  ) : (
                    <div style={{ position: 'relative', width: '200px' }}>
                      <input 
                        type="text" 
                        placeholder={formData.student || "Type to search clients..."}
                        value={clientFilter} 
                        onChange={(e) => setClientFilter(e.target.value)}
                        onFocus={() => setShowClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                        required={!formData.student}
                      />
                      {showClientDropdown && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', borderTop: 'none', maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                          {(Array.isArray(people) ? people : []).filter(person => {
                            if (clientFilter.length < 3) return true;
                            const firstName = (person.firstName || person.first_name || '').toLowerCase();
                            const lastName = (person.lastName || person.last_name || '').toLowerCase();
                            const filter = clientFilter.toLowerCase();
                            return firstName.includes(filter) || lastName.includes(filter);
                          }).map(person => {
                            const displayName = `${person.firstName || person.first_name || ''} ${person.lastName || person.last_name || ''}`.trim();
                            return (
                              <div key={person.id} 
                                   style={{ padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                   onMouseDown={() => {
                                     setFormData({
                                       ...formData,
                                       student: displayName,
                                       safetyPilotId: formData.safetyPilotId && parseInt(formData.safetyPilotId, 10) === person.id ? '' : formData.safetyPilotId
                                     });
                                     setSelectedClientData(person);
                                     setClientFilter('');
                                     setShowClientDropdown(false);
                                   }}
                                   onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                   onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}>
                                {displayName}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Type:</label>
                  <select aria-label="Reservation type" value={selectedReservationType ? String(selectedReservationType.id) : formData.type ? `legacy:${formData.type}` : ''} disabled={loadingReservationTypes} onChange={(e) => {
                    const selectedType = reservationTypes.find(type => String(type.id) === e.target.value);
                    const newType = legacyReservationType(selectedType?.name);
                    const updatedFormData = applyReservationDuration({ ...formData, type: newType, reservationTypeId: selectedType?.id ?? null }, selectedType);

                    if (newType !== 'Rental') {
                      updatedFormData.safetyPilotId = '';
                      updatedFormData.renterChargePercentage = '70';
                      setSafetyPilotFilter('');
                      setShowSafetyPilotDropdown(false);
                    } else if (!updatedFormData.renterChargePercentage) {
                      updatedFormData.renterChargePercentage = '70';
                    }
                    
                    setFormData(updatedFormData);
                  }} required>
                    <option value="">Select Type</option>
                    {!selectedReservationType && formData.type && <option value={`legacy:${formData.type}`}>{editingReservation?.type || formData.type}</option>}
                    {reservationTypes.filter(type => {
                      const name = legacyReservationType(type.name);
                      if (!['Rental', 'Solo'].includes(name) || !formData.aircraft || !formData.student || String(type.id) === String(selectedReservationType?.id)) return true;
                      const client = (Array.isArray(people) ? people : []).find(p => `${p.firstName || p.first_name} ${p.lastName || p.last_name}`.trim() === formData.student);
                      if (!client?.aircraftList) return true;
                      const clientAircraft = client.aircraftList.find(ac => ac.aircraftId == formData.aircraft);
                      const limitation = String(clientAircraft?.limitations || '').toLowerCase();
                      return ['instruct', 'rental'].includes(limitation) || (name === 'Solo' && limitation === 'solo');
                    }).map(type => <option key={type.id} value={String(type.id)}>{type.name}</option>)}
                  </select>
                </div>
                {loadingReservationTypes && <p role="status">Loading reservation types...</p>}
                {reservationTypesError && <p role="alert">{reservationTypesError} <button type="button" onClick={fetchReservationTypes}>Retry</button></p>}
                {!loadingReservationTypes && !reservationTypesError && !reservationTypes.length && <p>No reservation types configured.</p>}
                {selectedReservationType && <div style={{ marginBottom: '1rem' }}>
                  {selectedReservationType.description && <p>{selectedReservationType.description}</p>}
                  {selectedReservationType.cost != null && selectedReservationType.cost !== '' && <span>Cost: ${selectedReservationType.cost}</span>}
                </div>}
                {formData.type === 'Rental' && (
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <label style={{ marginTop: '0.5rem' }}>
                      Safety Pilot:
                      {(() => {
                        const statusRaw = editingReservation?.safetyPilotStatus;
                        const normalizedStatus = statusRaw === 'confirmed' || statusRaw === 'denied' || statusRaw === 'unconfirmed'
                          ? statusRaw
                          : (formData.safetyPilotId ? 'unconfirmed' : 'not-assigned');
                        const statusLabel = normalizedStatus === 'confirmed'
                          ? 'Confirmed'
                          : normalizedStatus === 'denied'
                            ? 'Denied'
                            : normalizedStatus === 'unconfirmed'
                              ? 'Unconfirmed'
                              : 'Not assigned';
                        const statusColor = normalizedStatus === 'confirmed'
                          ? '#2a7a4b'
                          : normalizedStatus === 'denied'
                            ? '#b02a37'
                            : '#6c757d';

                        return <span style={{ marginLeft: '0.35rem', color: statusColor, fontStyle: 'italic' }}>({statusLabel})</span>;
                      })()}
                    </label>
                    <div style={{ position: 'relative', width: '200px' }}>
                      <input
                        type="text"
                        placeholder={(() => {
                          const selected = (Array.isArray(availableUsers) ? availableUsers : []).find(user => user.id === parseInt(formData.safetyPilotId, 10)) ||
                            (Array.isArray(people) ? people : []).find(user => user.id === parseInt(formData.safetyPilotId, 10));
                          return selected ? getDisplayName(selected) : 'Type to search safety pilot...';
                        })()}
                        value={safetyPilotFilter}
                        onChange={(e) => setSafetyPilotFilter(e.target.value)}
                        onFocus={() => setShowSafetyPilotDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSafetyPilotDropdown(false), 200)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                      {showSafetyPilotDropdown && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', borderTop: 'none', maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                          {(Array.isArray(availableUsers) ? availableUsers : []).filter((user) => {
                            if (!hasEligibleSafetyPilotCertification(user)) return false;
                            if (safetyPilotFilter.length < 3) return true;
                            return getDisplayName(user).toLowerCase().includes(safetyPilotFilter.toLowerCase());
                          }).map(user => {
                            const displayName = getDisplayName(user);
                            return (
                              <div
                                key={`safety-${user.id}`}
                                style={{ padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                onMouseDown={() => {
                                  const selectedClientId = (Array.isArray(people) ? people : []).find(p => `${p.firstName || p.first_name || ''} ${p.lastName || p.last_name || ''}`.trim() === formData.student)?.id;
                                  if (selectedClientId && selectedClientId === user.id) {
                                    return;
                                  }
                                  if (!hasEligibleSafetyPilotCertification(user)) {
                                    return;
                                  }
                                  setFormData({ ...formData, safetyPilotId: `${user.id}`, renterChargePercentage: formData.renterChargePercentage || '70' });
                                  setSafetyPilotFilter('');
                                  setShowSafetyPilotDropdown(false);
                                }}
                                onMouseEnter={(event) => { event.target.style.backgroundColor = '#f0f0f0'; }}
                                onMouseLeave={(event) => { event.target.style.backgroundColor = 'white'; }}
                              >
                                {displayName}
                              </div>
                            );
                          })}
                          {(Array.isArray(availableUsers) ? availableUsers : []).filter((user) => hasEligibleSafetyPilotCertification(user)).length === 0 && (
                            <div style={{ padding: '0.5rem', color: '#666', fontStyle: 'italic' }}>
                              No available users with PPL, CPL, or ATP certification
                            </div>
                          )}
                          {formData.safetyPilotId && (
                            <div
                              style={{ padding: '0.5rem', cursor: 'pointer', color: '#dc3545', fontWeight: '500' }}
                              onMouseDown={() => {
                                setFormData({ ...formData, safetyPilotId: '' });
                                setSafetyPilotFilter('');
                                setShowSafetyPilotDropdown(false);
                              }}
                            >
                              Clear selection
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {formData.type === 'Rental' && formData.safetyPilotId && (
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Rental charge split:</label>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <label style={{ fontSize: '0.75rem', color: '#555' }}>Renter charge %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={formData.renterChargePercentage}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            if (rawValue === '') {
                              setFormData({ ...formData, renterChargePercentage: '' });
                              return;
                            }
                            const numericValue = Math.max(0, Math.min(100, parseFloat(rawValue)));
                            setFormData({ ...formData, renterChargePercentage: `${numericValue}` });
                          }}
                          style={{ width: '120px', padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <label style={{ fontSize: '0.75rem', color: '#555' }}>Safety pilot charge %</label>
                        <input
                          type="number"
                          value={(100 - (parseFloat(formData.renterChargePercentage) || 0)).toFixed(0)}
                          readOnly
                          style={{ width: '120px', padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                      <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                        These percentages are used to split rental charges.
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: '1rem' }}>
                  <label>Comments{formData.type === 'Flight Training' && selectedManeuvers.length === 0 ? ' (required unless a maneuver is selected)' : ':'}</label>
                  <textarea value={formData.comments} onChange={(e) => setFormData({...formData, comments: e.target.value})} required={formData.type === 'Flight Training' && selectedManeuvers.length === 0} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem' }} />
                </div>
              </>
            )}
            
            {activeTab === 'maneuvers' && showReservationManeuvers && (
              <div>
                {(() => {
                  const allManeuvers = Array.isArray(maneuvers) ? maneuvers : [];
                  const categories = [...new Set(allManeuvers.map(m => m.category || 'Maneuvers'))].sort();
                  const currentCat = categories.includes(activeManeuverCategory) ? activeManeuverCategory : (categories[0] || '');
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <label htmlFor="reservation-maneuver-category" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#495057' }}>Category:</label>
                        <select
                          id="reservation-maneuver-category"
                          value={currentCat}
                          onChange={(e) => setActiveManeuverCategory(e.target.value)}
                          style={{ padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: 'white', minWidth: '220px' }}
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div ref={maneuversPanelRef} style={{ border: '1px solid #ccc', padding: '0.75rem', backgroundColor: 'white', maxHeight: '260px', overflow: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                          {allManeuvers.filter(m => (m.category || 'Maneuvers') === currentCat).map(maneuver => (
                            <div
                              key={maneuver.id}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem' }}
                              onMouseEnter={(event) => handleManeuverMouseMove(event, maneuver)}
                              onMouseMove={(event) => handleManeuverMouseMove(event, maneuver)}
                              onMouseLeave={() => setHoveredManeuver(null)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedManeuvers.includes(maneuver.id)}
                                onChange={(e) => {
                                  if (currentUser?.user?.orgRole === 'client') return;
                                  if (e.target.checked) {
                                    setSelectedManeuvers([...selectedManeuvers, maneuver.id]);
                                  } else {
                                    setSelectedManeuvers(selectedManeuvers.filter(id => id !== maneuver.id));
                                  }
                                }}
                                disabled={currentUser?.user?.orgRole === 'client'}
                              />
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setSelectedManeuverDetails(maneuver);
                                }}
                                style={{
                                  fontSize: '0.9rem',
                                  color: '#007bff',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textAlign: 'left'
                                }}
                                title="View maneuver steps"
                              >
                                {maneuver.name}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      {selectedManeuvers.length > 0 && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                          {selectedManeuvers.length} maneuver{selectedManeuvers.length !== 1 ? 's' : ''} selected
                        </div>
                      )}

                      {hoveredManeuver && hoveredManeuverStepsContent.type !== 'none' && typeof document !== 'undefined' && ReactDOM.createPortal(
                        <div
                          style={{
                            position: 'fixed',
                            left: maneuverPopupLeft,
                            top: maneuverPopupPosition.y,
                            zIndex: 4000,
                            width: `${maneuverPopupWidth}px`,
                            maxWidth: `${maneuverPopupWidth}px`,
                            backgroundColor: '#ffffff',
                            border: '1px solid #b7c6d1',
                            borderRadius: '6px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                            padding: '0.6rem 0.75rem',
                            pointerEvents: 'none',
                            maxHeight: '85vh',
                            overflowY: 'auto'
                          }}
                        >
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f3b4d', marginBottom: '0.35rem' }}>
                            {hoveredManeuver.name} Steps
                          </div>
                          {hoveredManeuverStepsContent.type === 'html' ? (
                            <div
                              style={{ fontSize: '0.8rem', color: '#233741', lineHeight: 1.4 }}
                              dangerouslySetInnerHTML={{ __html: hoveredManeuverStepsContent.html }}
                            />
                          ) : (
                            <ol style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8rem', color: '#233741' }}>
                              {hoveredManeuverStepsContent.lines.map((step, index) => (
                                <li key={`${hoveredManeuver.id}-step-${index}`} style={{ marginBottom: '0.2rem' }}>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>,
                        document.body
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            
            {activeTab === 'lesson' && editingReservation && ((editingReservation.type === 'Flight Training' || editingReservation.type === 'Ground lesson' || editingReservation.type === 'Discovery' || editingReservation.type === 'Solo' || editingReservation.type === 'Rental' || editingReservation.type === 'Checkride') || !editingReservation.userIdInstructor) && (
              <div>
                {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser' || currentUser?.user?.orgRole === 'staff' || editingReservation.userIdInstructor === currentUser?.user?.id) ? (
                  <>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Simulator:</label>
                      <input type="number" step="0.1" value={lessonData.simulator} onChange={(e) => { const v = e.target.value; const fee = computeLessonTotalFrom({...lessonData, simulator: v}); setLessonData({...lessonData, simulator: v, fee}); }} onBlur={(e) => { const v = e.target.value; setLessonData({...lessonData, simulator: v === '' ? '' : v}); }} style={{ width: '140px' }} />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Flight Time:</label>
                      <input type="number" step="0.1" value={lessonData.flightTime || (aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd && aircraftData.hobbsStart ? (parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)).toFixed(1) : '')} onChange={(e) => setLessonData({...lessonData, flightTime: e.target.value})} onBlur={(e) => { setLessonData({...lessonData, flightTime: e.target.value === '' ? '' : e.target.value}); calculateLessonTotal(); }} style={{ width: '140px' }} />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Dual Received:</label>
                      <input type="number" step="0.1" value={lessonData.dualReceived} onChange={(e) => setLessonData({...lessonData, dualReceived: e.target.value})} onBlur={(e) => { setLessonData({...lessonData, dualReceived: e.target.value === '' ? '' : e.target.value}); calculateLessonTotal(); }} style={{ width: '140px', backgroundColor: !editingReservation.userIdInstructor ? '#f5f5f5' : 'white' }} readOnly={!editingReservation.userIdInstructor} />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Ground:</label>
                      <input type="number" step="0.1" value={lessonData.ground} onChange={(e) => setLessonData({...lessonData, ground: e.target.value})} onBlur={(e) => { setLessonData({...lessonData, ground: e.target.value === '' ? '' : e.target.value}); calculateLessonTotal(); }} style={{ width: '140px', backgroundColor: !editingReservation.userIdInstructor ? '#f5f5f5' : 'white' }} readOnly={!editingReservation.userIdInstructor} />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label>Debrief:</label>
                      <textarea value={lessonData.debrief} onChange={(e) => setLessonData({...lessonData, debrief: e.target.value})} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem' }} />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label>Next Flight:</label>
                      <textarea value={lessonData.nextFlight} onChange={(e) => setLessonData({...lessonData, nextFlight: e.target.value})} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem' }} />
                    </div>
                    <LessonFinancialFields value={lessonData} onChange={setLessonData} total={lessonFeeOverride ?? computeLessonTotalFrom(lessonData)} onTotalChange={setLessonFeeOverride} disabled={!canCurrentUserEditReservation(editingReservation)} onPaymentBlur={event => { const payment = event.target.value; setLessonData(previous => ({ ...previous, payment, credit: payment })); }} />
                    {lessonMessage && (
                      <div style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: lessonMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: lessonMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${lessonMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                        {lessonMessage}
                      </div>
                    )}
                    <div style={{ textAlign: 'center' }}>
                      <button type="button" onClick={handleSaveLesson} disabled={isSavingLesson} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: isSavingLesson ? 'not-allowed' : 'pointer', opacity: isSavingLesson ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        {isSavingLesson && <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⚙️</span>}
                        {isSavingLesson ? 'Saving...' : 'Save Lesson'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Simulator:</label>
                      <input type="number" step="0.1" value={lessonData.simulator} style={{ width: '140px', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Flight Time:</label>
                      <input type="number" step="0.1" value={lessonData.flightTime || (aircraftData && aircraftData.hobbsEnd && aircraftData.hobbsStart ? (parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)).toFixed(1) : '')} style={{ width: '140px', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Dual Received:</label>
                      <input type="number" step="0.1" value={lessonData.dualReceived} style={{ width: '140px', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Ground:</label>
                      <input type="number" step="0.1" value={lessonData.ground} style={{ width: '140px', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label>Debrief:</label>
                      <textarea value={lessonData.debrief} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label>Next Flight:</label>
                      <textarea value={lessonData.nextFlight} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem', backgroundColor: '#f5f5f5' }} readOnly />
                    </div>
                    <LessonFinancialFields value={lessonData} onChange={setLessonData} total={lessonFeeOverride ?? computeLessonTotalFrom(lessonData)} onTotalChange={setLessonFeeOverride} disabled={!canCurrentUserEditReservation(editingReservation)} onPaymentBlur={event => { const payment = event.target.value; setLessonData(previous => ({ ...previous, payment, credit: payment })); }} />
                    {lessonMessage && <p role="status">{lessonMessage}</p>}
                    {canCurrentUserEditReservation(editingReservation) && <div style={{ textAlign: 'center' }}><button type="button" disabled={isSavingLesson} onClick={handleSaveLesson} style={{ backgroundColor: '#007bff', color: 'white', padding: '0.75rem 1.5rem', border: 0, borderRadius: '4px' }}>{isSavingLesson ? 'Saving...' : 'Save Lesson'}</button></div>}
                  </>
                )}
              </div>
            )}
            
            {activeTab === 'flightLog' && editingReservation && (() => {
              const reservationAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingReservation.aircraftId);
              const flightTime = lessonData.flightTime || (aircraftData?.hobbsEnd && aircraftData?.hobbsStart ? (parseFloat(aircraftData.hobbsEnd) - parseFloat(aircraftData.hobbsStart)).toFixed(1) : '');
              const entry = reservationFlightLogEntry || {
                flightDate: editingReservation.startDate ? editingReservation.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
                aircraftMake: reservationAircraft?.make || reservationAircraft?.manufacturer || '',
                aircraftModel: reservationAircraft?.model || '',
                origin: aircraftData?.origin || '',
                route: aircraftData?.route || '',
                destination: aircraftData?.destination || '',
                dualReceived: lessonData.dualReceived || '',
                totalFlightTime: flightTime,
                asel: '', amel: '', pic: '', dualGiven: '', night: '', crossCountry: '',
                actualInstrument: '', simulatedInstrument: '', approaches: '',
                landingsDay: '', landingsNight: '', remarks: '', isSummary: false
              };
              return (
                <div style={{ padding: '1rem', marginTop: '-30px' }}>
                  <h4 style={{ margin: '0 0 1rem 0' }}>Flight Log Entry</h4>
                  {flightLogMessage && (
                    <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: flightLogMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: flightLogMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${flightLogMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>{flightLogMessage}</div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem', alignItems: 'end' }}>
                    {[['flightDate','Date','date'],['aircraftMake','Make','text'],['aircraftModel','Model','text'],['origin','From','text'],['route','Route','text'],['destination','To','text']].map(([f,label,type]) => (
                      <div key={f}>
                        <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '500' }}>{label}:</label>
                        <input type={type} value={entry[f] || ''} onChange={e => setReservationFlightLogEntry({...entry, [f]: e.target.value})} style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', marginBottom: '1rem', alignItems: 'end' }}>
                    {[['asel','ASEL'],['amel','AMEL'],['dualReceived','Dual Rcvd'],['pic','PIC'],['dualGiven','Dual Given'],['night','Night'],['crossCountry','XC'],['actualInstrument','Act Inst'],['simulatedInstrument','Sim Inst'],['approaches','Appr'],['landingsDay','Day Ldg'],['landingsNight','Night Ldg'],['totalFlightTime','Total']].map(([f,label]) => (
                      <div key={f}>
                        <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', fontWeight: '500' }}>{label}:</label>
                        <input type="number" step={['approaches','landingsDay','landingsNight'].includes(f) ? '1' : '0.1'} value={entry[f] ?? ''} onChange={e => setReservationFlightLogEntry({...entry, [f]: e.target.value})} style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
                      </div>
                    ))}
                    <div />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '500' }}>Remarks:</label>
                    <textarea value={entry.remarks || ''} onChange={e => setReservationFlightLogEntry({...entry, remarks: e.target.value})} rows="2" style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }} />
                  </div>
                  <button type="button" onClick={() => saveReservationFlightLogEntry(entry)} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Flight Log Entry</button>
                </div>
              );
            })()}

            {activeTab === 'aircraft' && editingReservation && editingReservation.aircraftId && (
              <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <strong>Hobbs</strong>
                    {checkInData.endHobbs && checkOutData.endHobbs && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#666' }}>Flight time: {(parseFloat(checkInData.endHobbs) - parseFloat(checkOutData.endHobbs)).toFixed(1)}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', width: '50%', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '50%' }}>
                      <span style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.25rem' }}>Start</span>
                      <input type="number" step="0.1" value={checkOutData.endHobbs} onChange={(e) => setCheckOutData({...checkOutData, endHobbs: e.target.value})} style={{ width: '100%', textAlign: 'right' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '50%' }}>
                      <span style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.25rem' }}>End</span>
                      <input type="number" step="0.1" value={checkInData.endHobbs} onChange={(e) => setCheckInData({...checkInData, endHobbs: e.target.value})} style={{ width: '100%', textAlign: 'right' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <strong>Tach</strong>
                    {checkInData.endTach && checkOutData.endTach && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#666' }}>Engine time: {(parseFloat(checkInData.endTach) - parseFloat(checkOutData.endTach)).toFixed(2)}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', width: '50%', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '50%' }}>
                      <span style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.25rem' }}>Start</span>
                      <input type="number" step="0.01" value={checkOutData.endTach} onChange={(e) => setCheckOutData({...checkOutData, endTach: e.target.value})} style={{ width: '100%', textAlign: 'right', backgroundColor: getTachBackgroundColor(checkOutData.endTach) }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '50%' }}>
                      <span style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.25rem' }}>End</span>
                      <input type="number" step="0.01" value={checkInData.endTach} onChange={(e) => setCheckInData({...checkInData, endTach: e.target.value})} style={{ width: '100%', textAlign: 'right', backgroundColor: getTachBackgroundColor(checkInData.endTach) }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                  </div>
                </div>
                {isCheckedOut && (
                  <>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Origin:</label>
                      <input type="text" value={checkInData.origin} onChange={(e) => setCheckInData({...checkInData, origin: e.target.value})} style={{ width: '140px' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Route:</label>
                      <input type="text" value={checkInData.route} onChange={(e) => setCheckInData({...checkInData, route: e.target.value})} style={{ width: '140px' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Destination:</label>
                      <input type="text" value={checkInData.destination} onChange={(e) => setCheckInData({...checkInData, destination: e.target.value})} style={{ width: '140px' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                    </div>
                  </>
                )}
                <div style={{ marginBottom: '1rem' }}>
                  <label>Remarks:</label>
                  <textarea value={checkOutData.remarks} onChange={(e) => setCheckOutData({...checkOutData, remarks: e.target.value})} rows="3" style={{ width: '100%', resize: 'vertical', marginTop: '0.5rem' }} disabled={aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd} />
                </div>

                <div style={{ marginBottom: '1rem', marginTop: '1.25rem', borderTop: '1px solid #d8d8d8', paddingTop: '1rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Open Squawks</strong>
                    </div>

                    {isCheckedOut && !(aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd) && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: 600 }}>Issue</label>
                            <input
                              type="text"
                              maxLength={200}
                              value={newReservationSquawk.issue || ''}
                              onChange={(e) => setNewReservationSquawk({ ...newReservationSquawk, issue: e.target.value })}
                              style={{ width: '100%', padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px' }}
                              placeholder="Short issue summary"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: 600 }}>Details</label>
                            <textarea
                              value={newReservationSquawk.details || ''}
                              onChange={(e) => setNewReservationSquawk({ ...newReservationSquawk, details: e.target.value })}
                              rows="3"
                              style={{ width: '100%', resize: 'vertical', minHeight: '68px', padding: '0.45rem', border: '1px solid #ccc', borderRadius: '4px' }}
                              placeholder="Maintenance details, symptoms, context"
                            />
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <div />
                          <button
                            type="button"
                            onClick={handleAddReservationSquawk}
                            style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '0.55rem 0.9rem', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Save Squawk
                          </button>
                        </div>
                      </>
                    )}

                    {reservationSquawkMessage && (
                      <div style={{ padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', backgroundColor: reservationSquawkMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: reservationSquawkMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${reservationSquawkMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                        {reservationSquawkMessage}
                      </div>
                    )}

                    {isLoadingReservationSquawks ? (
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading squawks...</div>
                    ) : (
                      <div style={{ width: '100%', maxWidth: '100%', maxHeight: '160px', overflowX: 'auto', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '640px', width: 'max-content', tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '22%' }} />
                            <col style={{ width: '52%' }} />
                          </colgroup>
                          <thead>
                            <tr style={{ backgroundColor: '#f7f7f7' }}>
                              <th style={{ padding: '0.4rem', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Reported</th>
                              <th style={{ padding: '0.4rem', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                              <th style={{ padding: '0.4rem', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Issue</th>
                              <th style={{ padding: '0.4rem', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reservationSquawks.length === 0 ? (
                              <tr>
                                <td colSpan="4" style={{ padding: '0.65rem', textAlign: 'center', color: '#666' }}>No Open Squawks</td>
                              </tr>
                            ) : reservationSquawks.map((sq) => (
                              <tr key={sq.id}>
                                <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                                  {sq.reportedDatetime ? new Date(sq.reportedDatetime).toLocaleDateString() : ''}
                                </td>
                                <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>{sq.status || ''}</td>
                                <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0' }}>{sq.issue || ''}</td>
                                <td style={{ padding: '0.4rem', borderBottom: '1px solid #f0f0f0', whiteSpace: 'pre-wrap' }}>{sq.details || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                {checkOutMessage && (
                  <div style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: checkOutMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: checkOutMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${checkOutMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                    {checkOutMessage}
                  </div>
                )}
                {isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach)) && (
                  <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', color: '#856404', fontSize: '0.9rem', textAlign: 'center' }}>
                    {!checkInData.endHobbs || !checkInData.endTach ? 'Please fill in both End Hobbs and End Tach' : 
                     parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) ? 'End Hobbs must be higher than Start Hobbs' :
                     parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach) ? 'End Tach must be higher than Start Tach' : ''}
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  {aircraftData && aircraftData.reservationId && aircraftData.hobbsEnd ? (
                    <div style={{ padding: '0.75rem', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: '4px' }}>Aircraft Already Checked In</div>
                  ) : (
                    <button type="button" onClick={isCheckedOut ? handleCheckIn : handleCheckOut} disabled={isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach))} style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer', opacity: (isCheckedOut && (!checkInData.endHobbs || !checkInData.endTach || parseFloat(checkInData.endHobbs) <= parseFloat(checkOutData.endHobbs) || parseFloat(checkInData.endTach) <= parseFloat(checkOutData.endTach))) ? 0.5 : 1 }}>{isCheckedOut ? 'Check In' : 'Check Out'}</button>
                  )}
                </div>
              </div>
            )}
            </fieldset>
            </form>
          
          {message && (
            <div style={{ textAlign: 'center', padding: '0.5rem', color: isError ? 'red' : 'green', fontWeight: 'bold', backgroundColor: '#f8f9fa', borderTop: '1px solid #ccc' }}>
              {message}
            </div>
          )}
          
          <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa' }}>
            {editingReservation && canCurrentUserConfirmSafetyPilot(editingReservation) && (
              <button type="button" onClick={handleConfirmSafetyPilot} disabled={isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting} style={{ marginRight: '1rem', backgroundColor: '#2a7a4b', color: 'white', opacity: (isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting) ? 0.6 : 1, cursor: (isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting) ? 'not-allowed' : 'pointer' }}>
                {isConfirmingSafetyPilot ? 'Confirming...' : 'Confirm Safety Pilot'}
              </button>
            )}
            {editingReservation && canCurrentUserDenySafetyPilot(editingReservation) && (
              <button type="button" onClick={handleDenySafetyPilot} disabled={isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting} style={{ marginRight: '1rem', backgroundColor: '#b02a37', color: 'white', opacity: (isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting) ? 0.6 : 1, cursor: (isConfirmingSafetyPilot || isDenyingSafetyPilot || isSubmitting) ? 'not-allowed' : 'pointer' }}>
                {isDenyingSafetyPilot ? 'Denying...' : 'Deny Safety Pilot'}
              </button>
            )}
            {!(editingReservation && isCurrentUserAssignedSafetyPilot(editingReservation)) && (
              <button type="submit" form="reservation-form" disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                {isSubmitting && <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⚙️</span>}
                {isSubmitting ? (editingReservation ? 'Updating...' : 'Creating...') : (editingReservation ? 'Update Reservation' : 'Create Reservation')}
              </button>
            )}
            {editingReservation && !isCurrentUserAssignedSafetyPilot(editingReservation) && (
              <button type="button" onClick={() => { setCancelReason(''); setCustomReason(''); setShowCancelModal(true); }} disabled={!canCancelReservationAtShortNotice(editingReservation, currentUser?.user)} title={!canCancelReservationAtShortNotice(editingReservation, currentUser?.user) ? 'Only an instructor, admin, or superuser can cancel a flight lesson within 24 hours.' : undefined} style={{ marginLeft: '1rem', backgroundColor: '#dc3545', color: 'white' }}>Cancel Reservation</button>
            )}
            {editingReservation && !isCurrentUserAssignedSafetyPilot(editingReservation) && !canCancelReservationAtShortNotice(editingReservation, currentUser?.user) && <p role="status">Only an instructor, admin, or superuser can cancel a flight lesson within 24 hours.</p>}
            <button type="button" onClick={() => { setShowReservationForm(false); setEditingReservation(null); setSelectedManeuvers([]); setActiveTab('basic'); setClientFilter(''); setSafetyPilotFilter(''); setShowSafetyPilotDropdown(false); setMessage(''); setIsSubmitting(false); setShowManeuverSelectionConfirmModal(false); setAircraftData(null); setAircraftMaintenanceData(null); setCheckOutData({ endHobbs: '', endTach: '', remarks: '' }); setCheckInData({ endHobbs: '', endTach: '', origin: '', route: '', destination: '' }); setCheckOutMessage(''); setIsCheckedOut(false); setLessonData({ simulator: '', dualReceived: '', ground: '', debrief: '', nextFlight: '', purchases: '', credit: '', flightTime: '', payment: '', paymentType: '', fee: '' }); setLessonMessage(''); setLessonId(null); setReservationFlightLogEntry(null); setFlightLogMessage(''); setReservationSquawks([]); setReservationSquawkMessage(''); setNewReservationSquawk({ issue: '', details: '' }); }} style={{ marginLeft: '1rem' }} disabled={isSubmitting}>Cancel</button>
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      {showAircraftModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={closeAircraftModal}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,0.15)', minWidth: '280px', maxWidth: '90%', position: 'relative' }}>
            <button onClick={closeAircraftModal} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            <h3 style={{ marginTop: 0 }}>Aircraft Details</h3>
            {selectedAircraftImages.length > 0 && (
              <div style={{ position: 'relative', width: '280px', maxWidth: '100%', margin: '0 auto 1rem' }}>
                <button type="button" onClick={() => setEnlargedScheduleImage({ url: selectedAircraftImages[selectedAircraftImageIndex].imageUrl, alt: selectedAircraftItem.tailNumber || selectedAircraftItem.registration || 'Aircraft', images: selectedAircraftImages.map((image) => image.imageUrl), index: selectedAircraftImageIndex })} title="View full-size aircraft image" style={{ display: 'block', padding: 0, border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'zoom-in', width: '100%' }}>
                  <img src={selectedAircraftImages[selectedAircraftImageIndex].imageUrl} alt={selectedAircraftItem.tailNumber || selectedAircraftItem.registration || 'Aircraft'} style={{ width: '280px', maxWidth: '100%', height: '170px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #dee2e6', display: 'block' }} />
                </button>
                {selectedAircraftImages.length > 1 && (
                  <>
                    {selectedAircraftImageIndex > 0 && <button type="button" onClick={() => setSelectedAircraftImageIndex(selectedAircraftImageIndex - 1)} aria-label="Previous aircraft image" style={{ position: 'absolute', left: '0.4rem', top: '50%', transform: 'translateY(-50%)', width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '20px', cursor: 'pointer' }}>‹</button>}
                    {selectedAircraftImageIndex < selectedAircraftImages.length - 1 && <button type="button" onClick={() => setSelectedAircraftImageIndex(selectedAircraftImageIndex + 1)} aria-label="Next aircraft image" style={{ position: 'absolute', right: '0.4rem', top: '50%', transform: 'translateY(-50%)', width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '20px', cursor: 'pointer' }}>›</button>}
                    <div style={{ position: 'absolute', bottom: '0.4rem', left: '50%', transform: 'translateX(-50%)', padding: '0.15rem 0.45rem', borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '0.72rem' }}>{selectedAircraftImageIndex + 1} / {selectedAircraftImages.length}</div>
                  </>
                )}
              </div>
            )}
            { (selectedAircraftItem?.make || selectedAircraftItem?.manufacturer) && (
              <div style={{ marginBottom: '0.5rem' }}><strong>Make:</strong> {selectedAircraftItem?.make || selectedAircraftItem?.manufacturer}</div>
            )}
            { selectedAircraftItem?.model && (
              <div style={{ marginBottom: '0.5rem' }}><strong>Model:</strong> {selectedAircraftItem.model}</div>
            )}
            { selectedAircraftItem?.equipment && (
              <div style={{ marginBottom: '0.5rem' }}><strong>Equipment:</strong> {selectedAircraftItem.equipment}</div>
            )}
            {(selectedAircraftItem?.rate !== undefined && selectedAircraftItem?.rate !== null && selectedAircraftItem?.rate !== '') && (
              <div style={{ marginBottom: '0.5rem' }}><strong>Rate:</strong> {`$${selectedAircraftItem.rate}`}</div>
            )}
            { selectedAircraftItem?.color && (
              <div style={{ marginBottom: '0.5rem' }}><strong>Color:</strong> {selectedAircraftItem.color}</div>
            )}
          </div>
        </div>
      )}

      {showInstructorModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={closeInstructorModal}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,0.15)', width: 'min(560px, 90vw)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box', position: 'relative' }}>
            <button onClick={closeInstructorModal} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            <h3 style={{ marginTop: 0 }}>Instructor Details</h3>
            {(() => {
              const instructorProfile = selectedInstructorItem?.profileImageUrl
                || (Array.isArray(people) ? people : []).find((person) => Number(person.id) === Number(selectedInstructorItem?.id))?.profileImageUrl;
              return instructorProfile ? (
                <button type="button" onClick={() => setEnlargedScheduleImage({ url: instructorProfile, alt: selectedInstructorItem?.firstName || selectedInstructorItem?.username || 'Instructor profile' })} title="View full-size profile image" style={{ display: 'block', margin: '0 auto 1rem', padding: 0, border: 'none', borderRadius: '50%', backgroundColor: 'transparent', cursor: 'zoom-in' }}>
                  <img src={instructorProfile} alt={selectedInstructorItem?.firstName || selectedInstructorItem?.username || 'Instructor'} style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '50%', border: '1px solid #dee2e6', display: 'block' }} />
                </button>
              ) : null;
            })()}
            <table aria-label="Instructor details" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.9rem', lineHeight: 1.5 }}>
              <tbody>
            { (selectedInstructorItem?.firstName || selectedInstructorItem?.first_name || selectedInstructorItem?.username) && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Name</th>
                <td style={instructorDetailValueStyle}>{selectedInstructorItem?.firstName || selectedInstructorItem?.first_name || selectedInstructorItem?.username}</td>
              </tr>
            )}
            { (selectedInstructorItem?.email || selectedInstructorItem?.emailAddress) && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Email</th>
                <td style={instructorDetailValueStyle}>{selectedInstructorItem?.email || selectedInstructorItem?.emailAddress}</td>
              </tr>
            )}
            { (selectedInstructorItem?.phone || selectedInstructorItem?.phoneNumber || selectedInstructorItem?.mobile) && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Phone</th>
                <td style={instructorDetailValueStyle}>{selectedInstructorItem?.phone || selectedInstructorItem?.phoneNumber || selectedInstructorItem?.mobile}</td>
              </tr>
            )}
            {(selectedInstructorItem?.rate !== undefined && selectedInstructorItem?.rate !== null && selectedInstructorItem?.rate !== '') && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Student Rate</th>
                <td style={instructorDetailValueStyle}>{`$${selectedInstructorItem.rate}`}</td>
              </tr>
            )}
            {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && isSelectedUserInstructor && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Dual Given (Past 24 Hours)</th>
                <td style={instructorDetailValueStyle}>
                {isLoadingSelectedInstructorHoursLast24
                  ? 'Loading...'
                  : `${Number(selectedInstructorHoursLast24 || 0).toFixed(1)} hrs`}
                </td>
              </tr>
            )}
            {selectedInstructorItem?.certifications && (
              <tr>
                <th scope="row" style={instructorDetailLabelStyle}>Certifications</th>
                <td style={instructorDetailValueStyle}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {selectedInstructorItem.certifications.split(',').map(c => c.trim()).filter(Boolean).map(cert => (
                    <span key={cert} style={{ backgroundColor: '#e8f4fd', color: '#1976d2', padding: '0.15rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600' }}>{cert}</span>
                  ))}
                </div>
                </td>
              </tr>
            )}
            {(() => {
              const approvedAircraft = (selectedInstructorItem?.aircraftList || []).filter(ac =>
                ac.limitations && ac.limitations.toLowerCase() === 'instruct'
              ).slice().sort((a, b) => {
                const planeA = (Array.isArray(aircraft) ? aircraft : []).find(item => item.id == a.aircraftId);
                const planeB = (Array.isArray(aircraft) ? aircraft : []).find(item => item.id == b.aircraftId);
                const orderDiff = toSortOrderValue(planeA?.sortOrder) - toSortOrderValue(planeB?.sortOrder);
                if (orderDiff !== 0) return orderDiff;
                const labelA = planeA ? `${planeA.make || planeA.manufacturer || ''} ${planeA.model || ''} ${planeA.tailNumber || planeA.registration || ''}`.toLowerCase() : '';
                const labelB = planeB ? `${planeB.make || planeB.manufacturer || ''} ${planeB.model || ''} ${planeB.tailNumber || planeB.registration || ''}`.toLowerCase() : '';
                return labelA.localeCompare(labelB);
              });
              if (approvedAircraft.length === 0) return null;
              return (
                <tr>
                  <th scope="row" style={instructorDetailLabelStyle}>Approved to instruct in</th>
                  <td style={instructorDetailValueStyle}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {approvedAircraft.map(ac => {
                      // eslint-disable-next-line eqeqeq
                      const plane = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id == ac.aircraftId);
                      const label = plane
                        ? `${plane.make || plane.manufacturer || ''} ${plane.model || ''} ${plane.tailNumber || plane.registration || ''}`.trim()
                        : `Aircraft #${ac.aircraftId}`;
                      return (
                        <span key={ac.aircraftId} style={{ backgroundColor: '#e8f5e8', color: '#2e7d32', padding: '0.15rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600' }}>{label}</span>
                      );
                    })}
                  </div>
                  </td>
                </tr>
              );
            })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {enlargedScheduleImage && (
        <div onClick={() => setEnlargedScheduleImage(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, cursor: 'zoom-out' }}>
          <div onClick={(event) => event.stopPropagation()} style={{ position: 'relative', backgroundColor: 'white', padding: '1rem', borderRadius: '8px', maxWidth: '92vw', maxHeight: '92vh' }}>
            <button type="button" onClick={() => setEnlargedScheduleImage(null)} style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '18px', zIndex: 1 }}>×</button>
            {enlargedScheduleImage.images?.length > 1 && enlargedScheduleImage.index > 0 && <button type="button" onClick={() => setEnlargedScheduleImage((current) => { const index = current.index - 1; setSelectedAircraftImageIndex(index); return { ...current, index, url: current.images[index] }; })} aria-label="Previous image" style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.68)', color: 'white', fontSize: '26px', cursor: 'pointer', zIndex: 1 }}>‹</button>}
            <img src={enlargedScheduleImage.url} alt={enlargedScheduleImage.alt} style={{ maxWidth: '88vw', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
            {enlargedScheduleImage.images?.length > 1 && enlargedScheduleImage.index < enlargedScheduleImage.images.length - 1 && <button type="button" onClick={() => setEnlargedScheduleImage((current) => { const index = current.index + 1; setSelectedAircraftImageIndex(index); return { ...current, index, url: current.images[index] }; })} aria-label="Next image" style={{ position: 'absolute', right: '1.25rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.68)', color: 'white', fontSize: '26px', cursor: 'pointer', zIndex: 1 }}>›</button>}
            {enlargedScheduleImage.images?.length > 1 && <div style={{ position: 'absolute', left: '50%', bottom: '1.25rem', transform: 'translateX(-50%)', padding: '0.2rem 0.55rem', borderRadius: '12px', backgroundColor: 'rgba(0,0,0,0.68)', color: 'white', fontSize: '0.78rem' }}>{enlargedScheduleImage.index + 1} / {enlargedScheduleImage.images.length}</div>}
          </div>
        </div>
      )}

      {showCancelModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px', position: 'relative' }}>
            {isCancelling && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', zIndex: 1 }}>
                <div style={{ animation: 'spin 1s linear infinite', fontSize: '2rem' }}>⚙️</div>
              </div>
            )}
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Cancel Reservation</h3>
            <p style={{ margin: '0 0 1rem 0' }}>{requiresReservationReason(editingReservation) ? 'Please select a reason for cancellation within the next 28 hours:' : 'Are you sure you want to cancel this reservation?'}</p>
            {requiresReservationReason(editingReservation) && <div style={{ marginBottom: '1rem' }}>
              {['Illness - client', 'Illness - instructor', 'Schedule Conflict - client', 'Schedule Conflict - instructor', 'Weather - Outside of instructor\'s minimums', 'Weather - Outside of client\'s minimums', 'Aircraft maintenance', 'other'].map(reason => (
                <label key={reason} style={{ display: 'block', marginBottom: '0.5rem' }}>
                  <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={(e) => setCancelReason(e.target.value)} style={{ marginRight: '0.5rem' }} disabled={isCancelling} />
                  {reason === 'other' ? 'Other' : reason}
                </label>
              ))}
            </div>}
            {cancelReason === 'other' && (
              <div style={{ marginBottom: '1rem' }}>
                <input type="text" placeholder="Please specify..." value={customReason} onChange={(e) => setCustomReason(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} disabled={isCancelling} />
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={handleCancelReservation} disabled={isCancelling || !canCancelReservationAtShortNotice(editingReservation, currentUser?.user) || (requiresReservationReason(editingReservation) && (!cancelReason || (cancelReason === 'other' && !customReason.trim())))} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', opacity: isCancelling ? 0.6 : 1 }}>Confirm Cancel</button>
              <button onClick={() => { setShowCancelModal(false); setCancelReason(''); setCustomReason(''); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }} disabled={isCancelling}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showRescheduleModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Reservation Change Reason</h3>
            <p style={{ margin: '0 0 1rem 0' }}>Please select a reason for changing a reservation within the next 28 hours:</p>
            <div style={{ marginBottom: '1rem' }}>
              {['Illness - client', 'Illness - instructor', 'Schedule Conflict - client', 'Schedule Conflict - instructor', 'Weather - Outside of instructor\'s minimums', 'Weather - Outside of client\'s minimums', 'Aircraft maintenance', 'other'].map(reason => (
                <label key={reason} style={{ display: 'block', marginBottom: '0.5rem' }}>
                  <input type="radio" name="rescheduleReason" value={reason} checked={rescheduleReason === reason} onChange={(e) => setRescheduleReason(e.target.value)} style={{ marginRight: '0.5rem' }} />
                  {reason === 'other' ? 'Other' : reason}
                </label>
              ))}
            </div>
            {rescheduleReason === 'other' && (
              <div style={{ marginBottom: '1rem' }}>
                <input type="text" placeholder="Please specify..." value={rescheduleCustomReason} onChange={(e) => setRescheduleCustomReason(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} />
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                disabled={isSubmitting || !rescheduleReason || (rescheduleReason === 'other' && !rescheduleCustomReason.trim())}
                onClick={async () => {
                  const finalReason = (rescheduleReason === 'other' ? rescheduleCustomReason : rescheduleReason).trim();
                  if (!pendingSubmitPayload || isSubmitting || !finalReason) return;
                  const payload = { ...pendingSubmitPayload, rescheduleReason: finalReason };
                  const aircraftError = aircraftLocationError(payload.aircraftId,
                    new Date(schoolWallClockToIso(String(payload.startDate).slice(0, 10), convertMilitaryToTime(payload.startTime))),
                    new Date(schoolWallClockToIso(String(payload.endDate || payload.startDate).slice(0, 10), convertMilitaryToTime(payload.endTime))),
                    payload.locationId || selectedLocationId);
                  if (aircraftError) { window.alert(aircraftError); return; }
                  setShowRescheduleModal(false);
                  setIsSubmitting(true);
                  try {
                    const response = await fetch(`${API_BASE_URL}/reservation`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
                      body: JSON.stringify(payload)
                    });
                    if (response.ok) {
                      const { startDate, endDate } = getDateRange();
                      fetchReservations(startDate, endDate);
                      setMessage('Reservation Updated Successfully');
                      setIsError(false);
                      setTimeout(() => {
                        setShowReservationForm(false);
                        setEditingReservation(null);
                        setFormData({ date: '', time: '', endDate: '', endTime: '', instructor: '', aircraft: '', student: '', safetyPilotId: '', renterChargePercentage: '70', type: '', comments: '' });
                        setSelectedManeuvers([]);
                        setActiveTab('basic');
                        setClientFilter('');
                        setSafetyPilotFilter('');
                        setMessage('');
                      }, 2000);
                    } else {
                      const errorText = await response.text();
                      let errorMessage = 'Failed to update reservation';
                      try { errorMessage = JSON.parse(errorText).error || errorMessage; } catch {}
                      setMessage(`Error: ${errorMessage}`);
                      setIsError(true);
                    }
                  } catch { setMessage('Error: Error updating reservation'); setIsError(true); }
                  finally {
                    setIsSubmitting(false);
                    setRescheduleReason('');
                    setRescheduleCustomReason('');
                    setPendingSubmitPayload(null);
                  }
                }}
                style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >Confirm Changes</button>
              <button onClick={() => { setShowRescheduleModal(false); setRescheduleReason(''); setRescheduleCustomReason(''); setPendingSubmitPayload(null); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showManeuverSelectionConfirmModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '520px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Reservation</h3>
            <p style={{ margin: '0 0 1.25rem 0', lineHeight: 1.4 }}>
              You have selected Flight Training but did not select any maneuvers. Are you sure you would like to continue?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setShowManeuverSelectionConfirmModal(false)}
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowManeuverSelectionConfirmModal(false);
                  handleSubmit(null, true);
                }}
                style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showReservationForm && selectedManeuverDetails && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2200 }}
          onClick={() => setSelectedManeuverDetails(null)}
        >
          <div
            style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,0.15)', width: '560px', maxWidth: '92vw', maxHeight: '80vh', overflow: 'auto', position: 'relative' }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedManeuverDetails(null)}
              style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: '#007bff', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}
              aria-label="Close maneuver steps"
            >
              ×
            </button>

            <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>{selectedManeuverDetails.name} Steps</h3>

            {selectedManeuverStepsContent.type === 'html' ? (
              <div
                style={{ fontSize: '0.9rem', color: '#233741', lineHeight: 1.4 }}
                dangerouslySetInnerHTML={{ __html: selectedManeuverStepsContent.html }}
              />
            ) : selectedManeuverStepsContent.type === 'text' ? (
              <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', color: '#233741' }}>
                {selectedManeuverStepsContent.lines.map((step, index) => (
                  <li key={`${selectedManeuverDetails.id || selectedManeuverDetails.name}-modal-step-${index}`} style={{ marginBottom: '0.35rem' }}>
                    {step}
                  </li>
                ))}
              </ol>
            ) : (
              <div style={{ color: '#666', fontSize: '0.9rem' }}>No steps available for this maneuver.</div>
            )}

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setSelectedManeuverDetails(null)}
                style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1.1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <UserDialog 
        showDialog={showClientDialog}
        onClose={() => setShowClientDialog(false)}
        userData={selectedClientData}
        isReadOnly={true}
        allowProgressEdit={currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser' || (currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3)}
        title={selectedClientData ? <><span style={{ color: '#666' }}>Client Details - </span>{selectedClientData.firstName} {selectedClientData.lastName}</> : null}
      />
    </div>
  );
};

export default CalendarView;